// NyanyukiGuide.jsx — Complete Guide + Challenge MVP System with Percentage-Based Daily Targets
// Version 12.0 - Updated to match Nyanyuki Pro 8-mode bot with strict even/odd colors
// Includes V-Loss for all contract types, digit triggers, step-by-step trading process, $0.35 minimum stake awareness

import React, { useState, useEffect, useRef } from "react";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================
// CHALLENGE MVP DATA & CONSTANTS
// ============================================================

const MINIMUM_STAKE = 0.35;

const RECOMMENDED_PERCENTAGES = {
  CONSERVATIVE: {
    name: '🛡️ Conservative',
    range: [3, 5],
    color: '#00e676',
    bg: 'rgba(0,230,118,0.08)',
    description: 'Safe growth - 3-5% daily',
    stakeMultiplier: 0.02,
    maxDailyLoss: 5,
    recommendedStake: MINIMUM_STAKE
  },
  MODERATE: {
    name: '⚡ Moderate',
    range: [6, 10],
    color: '#ffd600',
    bg: 'rgba(255,214,0,0.08)',
    description: 'Balanced growth - 6-10% daily',
    stakeMultiplier: 0.03,
    maxDailyLoss: 8,
    recommendedStake: MINIMUM_STAKE * 2
  },
  AGGRESSIVE: {
    name: '🔥 Aggressive',
    range: [12, 20],
    color: '#ff5252',
    bg: 'rgba(255,82,82,0.08)',
    description: 'High growth - 12-20% daily',
    stakeMultiplier: 0.05,
    maxDailyLoss: 15,
    recommendedStake: MINIMUM_STAKE
  }
};

const SESSIONS_PER_DAY = 3;

// ============================================================
// CHALLENGE CALCULATION ENGINE WITH PERCENTAGE-BASED TARGETS
// ============================================================

class ChallengeCalculator {
  constructor(startCapital, dailyPercentage, customDays = null) {
    this.startCapital = parseFloat(startCapital) || 100;
    this.dailyPercentage = parseFloat(dailyPercentage) || 5;
    this.customDays = customDays ? parseInt(customDays) : null;
  }

  calculate() {
    if (this.startCapital <= 0) this.startCapital = 100;
    if (this.dailyPercentage <= 0) this.dailyPercentage = 5;

    let daysNeeded;
    if (this.customDays && this.customDays > 0) {
      daysNeeded = this.customDays;
    } else {
      daysNeeded = 30;
    }
    
    const challengeTable = this.generatePercentageTable(
      this.startCapital,
      this.dailyPercentage,
      daysNeeded
    );
    
    const finalCapital = challengeTable.finalCapital;
    const totalProfit = finalCapital - this.startCapital;
    const totalReturn = ((finalCapital / this.startCapital) - 1) * 100;
    
    const initialDailyTarget = this.startCapital * (this.dailyPercentage / 100);
    const initialSessionTarget = initialDailyTarget / SESSIONS_PER_DAY;
    
    const baseStake = Math.max(MINIMUM_STAKE, this.startCapital * 0.02);
    
    return {
      startCapital: this.startCapital,
      dailyPercentage: this.dailyPercentage,
      totalReturn: totalReturn.toFixed(2),
      dailyTarget: initialDailyTarget.toFixed(2),
      sessionTarget: initialSessionTarget.toFixed(2),
      daysNeeded: Math.max(1, Math.ceil(daysNeeded)),
      weeksNeeded: Math.max(1, Math.ceil(daysNeeded / 7)),
      baseStake: baseStake.toFixed(2),
      maxDailyLoss: (this.startCapital * 0.05).toFixed(2),
      stopLossLevel: (this.startCapital * 0.85).toFixed(2),
      challengeTable: challengeTable.table,
      finalCapital: finalCapital.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      minimumStake: MINIMUM_STAKE,
      recommendedProfile: this.getRecommendedProfile()
    };
  }

  generatePercentageTable(startCapital, dailyPercentage, daysNeeded) {
    const table = [];
    let currentCapital = startCapital;
    const totalDays = Math.min(Math.ceil(daysNeeded) || 30, 365);
    const dailyRate = dailyPercentage / 100;
    
    for (let day = 1; day <= totalDays; day++) {
      const dailyTarget = currentCapital * dailyRate;
      const sessionTarget = dailyTarget / SESSIONS_PER_DAY;
      const newCapital = currentCapital + dailyTarget;
      
      const row = {
        day,
        session1: `$${sessionTarget.toFixed(2)}`,
        session2: `$${sessionTarget.toFixed(2)}`,
        session3: `$${sessionTarget.toFixed(2)}`,
        dailyTarget: `$${dailyTarget.toFixed(2)}`,
        dailyPercentage: `${(dailyRate * 100).toFixed(2)}%`,
        startCapital: `$${currentCapital.toFixed(2)}`,
        endCapital: `$${newCapital.toFixed(2)}`,
        profit: `$${dailyTarget.toFixed(2)}`,
        runningProfit: `$${(newCapital - startCapital).toFixed(2)}`
      };
      
      table.push(row);
      currentCapital = newCapital;
    }
    
    return {
      table,
      finalCapital: currentCapital
    };
  }

  getRecommendedProfile() {
    if (this.dailyPercentage <= 5) {
      return RECOMMENDED_PERCENTAGES.CONSERVATIVE;
    } else if (this.dailyPercentage <= 10) {
      return RECOMMENDED_PERCENTAGES.MODERATE;
    } else {
      return RECOMMENDED_PERCENTAGES.AGGRESSIVE;
    }
  }
}

// ============================================================
// PDF GENERATION FUNCTION
// ============================================================

const generatePDF = (result, formData) => {
  try {
    const doc = new jsPDF();
    
    doc.setFontSize(22);
    doc.setTextColor(0, 230, 118);
    doc.text('Nyanyuki Pro Challenge Plan', 20, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 30);
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('Challenge Details', 20, 45);
    
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    doc.text(`Starting Capital: $${result.startCapital}`, 20, 55);
    doc.text(`Daily Target: ${result.dailyPercentage}%`, 20, 62);
    doc.text(`Duration: ${result.daysNeeded} days (${result.weeksNeeded} weeks)`, 20, 69);
    doc.text(`Minimum Trade Stake: $${result.minimumStake}`, 20, 76);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 230, 118);
    doc.text('Projections', 20, 91);
    
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    doc.text(`Initial Daily Target: $${result.dailyTarget}`, 20, 101);
    doc.text(`Initial Per Session: $${result.sessionTarget}`, 20, 108);
    doc.text(`Final Capital: $${result.finalCapital}`, 20, 115);
    doc.text(`Total Profit: $${result.totalProfit}`, 20, 122);
    doc.text(`Total Return: ${result.totalReturn}%`, 20, 129);
    doc.text(`Recommended Base Stake: $${result.baseStake}`, 20, 136);
    
    doc.setFontSize(14);
    doc.setTextColor(255, 214, 0);
    doc.text('Risk Management', 20, 151);
    
    doc.setFontSize(11);
    doc.setTextColor(200, 200, 200);
    doc.text(`Recommended Max Daily Loss: $${result.maxDailyLoss}`, 20, 161);
    doc.text(`Recommended Stop Loss: $${result.stopLossLevel}`, 20, 168);
    
    if (result.recommendedProfile) {
      doc.setFontSize(14);
      doc.setTextColor(result.recommendedProfile.color);
      doc.text('Risk Profile', 20, 183);
      
      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      doc.text(`${result.recommendedProfile.name}: ${result.recommendedProfile.description}`, 20, 193);
      doc.text(`Minimum Stake: $${result.minimumStake} (Deriv requirement)`, 20, 200);
    }
    
    doc.addPage();
    doc.setFontSize(16);
    doc.setTextColor(0, 230, 118);
    doc.text('Challenge Progress Table (Compound Interest)', 20, 20);
    
    const tableData = result.challengeTable.slice(0, 30).map(row => [
      `Day ${row.day}`,
      row.startCapital,
      row.dailyPercentage,
      row.session1,
      row.dailyTarget,
      row.profit,
      row.endCapital,
      row.runningProfit
    ]);
    
    autoTable(doc, {
      startY: 30,
      head: [['Day', 'Start Capital', 'Daily %', 'Per Session', 'Daily Target', 'Daily Profit', 'End Capital', 'Total Profit']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 2,
        textColor: [255, 255, 255],
        fillColor: [20, 20, 30],
      },
      headStyles: {
        fillColor: [0, 230, 118],
        textColor: [0, 0, 0],
        fontSize: 8,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [30, 30, 40],
      },
    });
    
    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('IMPORTANT RISK WARNING:', 20, finalY);
    doc.text('Trading involves significant risk of loss. This plan is for educational purposes only.', 20, finalY + 5);
    doc.text('Always start with a demo account and never risk more than you can afford to lose.', 20, finalY + 10);
    doc.text(`Minimum trade amount on Deriv is $${MINIMUM_STAKE}. Plan your stakes accordingly.`, 20, finalY + 15);
    doc.text(`Generated by Nyanyuki Pro Challenge Planner - ${new Date().getFullYear()}`, 20, finalY + 25);
    
    doc.save(`nyanyuki-challenge-${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('PDF Generation Error:', error);
    alert('Error generating PDF. Please try again or use CSV export.');
  }
};

// ============================================================
// NYANYUKI PRO ORIGINAL DATA - UPDATED WITH ALL 8 MODES
// ============================================================

const CONTRACT_TYPES = [
  {
    id: "DIGITOVER",
    label: "OVER",
    color: "#00e676",
    bg: "rgba(0,230,118,0.08)",
    border: "rgba(0,230,118,0.22)",
    winRate: "40%",
    payout: "~2.2×",
    example: "OVER 5",
    winOn: "6, 7, 8, 9",
    loseOn: "0, 1, 2, 3, 4, 5",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "HEDGE", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins if the last digit of the next tick is strictly GREATER than your barrier. With barrier 5 (OVER 5), you win on digits 6, 7, 8, 9 — four of ten possible digits.",
    deepDetail: "OVER is especially powerful when you observe the Digit Circle showing a cluster of high digits. In Hedge mode, OVER is paired with UNDER 4 to cover 80% of digits.",
    vLossCondition: "V-Loss increments when the last digit is ≤ your barrier. Code: isVLoss = (digit <= pred)",
    vLossExample: "If you set OVER 5 and digits 0,1,2,3,4,5 appear → each increments V-Loss counter. When counter reaches limit, trade fires.",
  },
  {
    id: "DIGITUNDER",
    label: "UNDER",
    color: "#ff5252",
    bg: "rgba(255,82,82,0.08)",
    border: "rgba(255,82,82,0.22)",
    winRate: "40%",
    payout: "~2.2×",
    example: "UNDER 4",
    winOn: "0, 1, 2, 3",
    loseOn: "4, 5, 6, 7, 8, 9",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "HEDGE", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins if the last digit is strictly LESS than your barrier. With barrier 4 (UNDER 4), you win on digits 0, 1, 2, 3 — four digits out of ten.",
    deepDetail: "UNDER is the mirror of OVER. In Hedge mode the bot always fires UNDER with barrier 4 simultaneously with OVER barrier 5.",
    vLossCondition: "V-Loss increments when the last digit is ≥ your barrier. Code: isVLoss = (digit >= pred)",
    vLossExample: "If you set UNDER 4 and digits 4,5,6,7,8,9 appear → each increments V-Loss counter.",
  },
  {
    id: "DIGITEVEN",
    label: "EVEN",
    color: "#448aff",
    bg: "rgba(68,138,255,0.08)",
    border: "rgba(68,138,255,0.22)",
    winRate: "50%",
    payout: "~1.95×",
    example: "EVEN",
    winOn: "0, 2, 4, 6, 8",
    loseOn: "1, 3, 5, 7, 9",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins if the last digit is even: 0, 2, 4, 6, or 8. Exactly 5 of 10 digits — 50% theoretical win rate.",
    deepDetail: "EVEN and ODD are the most balanced contract types. Digits are strictly color-coded: EVEN = GREEN (#10B981).",
    vLossCondition: "V-Loss increments when last digit is ODD. Code: isVLoss = (digit % 2 !== 0)",
    vLossExample: "If you bet EVEN and digits 1,3,5,7,9 appear → each increments V-Loss counter.",
  },
  {
    id: "DIGITODD",
    label: "ODD",
    color: "#ffd600",
    bg: "rgba(255,214,0,0.08)",
    border: "rgba(255,214,0,0.22)",
    winRate: "50%",
    payout: "~1.95×",
    example: "ODD",
    winOn: "1, 3, 5, 7, 9",
    loseOn: "0, 2, 4, 6, 8",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins if the last digit is odd: 1, 3, 5, 7, or 9. Exactly 5 of 10 digits — 50% theoretical win rate.",
    deepDetail: "ODD is the default Type 2 contract in Sequence mode. Digits are strictly color-coded: ODD = RED (#EF4444).",
    vLossCondition: "V-Loss increments when last digit is EVEN. Code: isVLoss = (digit % 2 === 0)",
    vLossExample: "If you bet ODD and digits 0,2,4,6,8 appear → each increments V-Loss counter.",
  },
  {
    id: "DIGITMATCH",
    label: "MATCH",
    color: "#e040fb",
    bg: "rgba(224,64,251,0.08)",
    border: "rgba(224,64,251,0.22)",
    winRate: "10%",
    payout: "~8×",
    example: "MATCH 7",
    winOn: "Only your exact digit",
    loseOn: "All other 9 digits",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins only if the last digit exactly matches your prediction. One specific digit out of 10 — 10% win probability.",
    deepDetail: "MATCH is the highest-variance contract type. In Stable mode, you set three MATCH predictions that cycle on losses.",
    vLossCondition: "V-Loss increments when last digit does NOT match your prediction. Code: isVLoss = (digit !== pred)",
    vLossExample: "If you bet MATCH 7 and digits 0,1,2,3,4,5,6,8,9 appear → each increments V-Loss counter.",
  },
  {
    id: "DIGITDIFF",
    label: "DIFF",
    color: "#ff9100",
    bg: "rgba(255,145,0,0.08)",
    border: "rgba(255,145,0,0.22)",
    winRate: "90%",
    payout: "~1.06×",
    example: "DIFF 5",
    winOn: "Any digit except your chosen one",
    loseOn: "Only your chosen digit",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "Wins if the last digit is ANYTHING EXCEPT your specified digit. 9 of 10 possible outcomes win — 90% probability.",
    deepDetail: "DIFF is the inverse of MATCH. The 90% win rate with tiny payout means flat staking produces steady but small gains.",
    vLossCondition: "V-Loss increments when last digit equals your prediction. Code: isVLoss = (digit === pred)",
    vLossExample: "If you bet DIFF 5 and digit 5 appears → V-Loss increments by 1.",
  },
  {
    id: "CALL",
    label: "RISE",
    color: "#00e676",
    bg: "rgba(0,230,118,0.08)",
    border: "rgba(0,230,118,0.22)",
    winRate: "~50%",
    payout: "~1.95×",
    example: "RISE (1 tick)",
    winOn: "Next tick price is HIGHER than entry price",
    loseOn: "Next tick price is equal or lower",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "RISE (internally called CALL) wins if the tick price at expiry is strictly higher than the price when the trade was placed.",
    deepDetail: "RISE/FALL is the only non-digit contract type. When selected, the interface shows a price direction indicator (▲/▼).",
    vLossCondition: "For RISE: V-Loss fires when price goes DOWN. Code: isVLoss = price <= prevPrice",
    vLossExample: "If you bet RISE and price goes down or stays same → V-Loss increments.",
  },
  {
    id: "PUT",
    label: "FALL",
    color: "#ff5252",
    bg: "rgba(255,82,82,0.08)",
    border: "rgba(255,82,82,0.22)",
    winRate: "~50%",
    payout: "~1.95×",
    example: "FALL (1 tick)",
    winOn: "Next tick price is LOWER than entry price",
    loseOn: "Next tick price is equal or higher",
    usedIn: ["STABLE", "AGGRESSIVE", "SEQUENCE", "STRIKE", "PARALLEL", "MANUAL"],
    explain: "FALL (internally called PUT) wins if the tick price at expiry is strictly lower than the entry price.",
    deepDetail: "FALL is most useful when you observe a series of consecutive rising ticks and predict a reversion.",
    vLossCondition: "V-Loss fires when price goes UP. Code: isVLoss = price >= prevPrice",
    vLossExample: "If you bet FALL and price goes up or stays same → V-Loss increments.",
  },
];

const MODES_DATA = [
  {
    id: "STABLE",
    label: "STABLE",
    emoji: "🛡️",
    color: "#007AFF",
    bg: "rgba(0,122,255,0.08)",
    border: "rgba(0,122,255,0.22)",
    riskLevel: "LOW",
    contracts: ["Any — typically DIGITMATCH"],
    triggerType: "V-Loss counter or Digit Trigger",
    vLossUsed: true,
    martingaleUsed: true,
    summary: "Stable uses a virtual V-Loss counter watching each tick. The bot watches silently, and only fires a trade when the V-Loss counter reaches your configured limit OR when a trigger digit appears.",
    howVLossWorks: "The V-Loss counter increments on every tick where the digit WOULD have been a loss. When the counter reaches your V-Loss limit, the bot fires the actual trade. After the trade result, the counter resets.",
    howTriggerWorks: "You can choose DIGIT TRIGGER mode: fire trade when a specific digit appears. Or V-LOSS mode: fire after a streak of virtual losses.",
    howMartingaleWorks: "After a trade LOSS: stableMartingaleCounter increments, next stake = baseStake × martingale^counter. After a WIN: counter resets to 0, stake returns to base.",
    configFields: [
      { name: "Contract Type", desc: "Choose any contract type. DIGITMATCH is most common." },
      { name: "Pred 1 / 2 / 3", desc: "Three prediction digits cycled through on consecutive losses." },
      { name: "Stake", desc: `Base stake in USD. Minimum $${MINIMUM_STAKE} on Deriv.` },
      { name: "Trigger Mode", desc: "Choose DIGIT TRIGGER (fire on specific digits) or V-LOSS (fire after streak)." },
      { name: "Triggers", desc: "Comma-separated digits that trigger a trade (e.g., '8,9')." },
      { name: "V-Loss Limit", desc: "Number of virtual losses before firing. 0 = every-tick mode." },
      { name: "Martingale", desc: "Stake multiplier after a loss. 2.0 = double stake." },
      { name: "Take Profit / Stop Loss", desc: "Dollar amounts to stop the bot." },
    ],
    tips: [
      `Minimum stake is $${MINIMUM_STAKE} on Deriv. Always respect this minimum.`,
      "Set V-Loss to 3–6. With DIGITMATCH (10% win rate), seeing the digit absent 4+ times makes the eventual hit more likely.",
      "Use cold digits from the Digit Circle for Pred1/2/3. Cold = underrepresented = statistically due.",
    ],
  },
  {
    id: "AGGRESSIVE",
    label: "AGGRESSIVE",
    emoji: "⚡",
    color: "#ff3d3d",
    bg: "rgba(255,61,61,0.08)",
    border: "rgba(255,61,61,0.22)",
    riskLevel: "HIGH",
    contracts: ["Any — typically DIGITEVEN or DIGITODD"],
    triggerType: "Every tick OR V-Loss OR Digit Trigger",
    vLossUsed: true,
    martingaleUsed: true,
    summary: "Aggressive mode fires on every tick when V-Loss=0, or waits for trigger conditions like Stable. High frequency, small stakes.",
    howVLossWorks: "Identical to Stable mode. V-Loss = 0 → every-tick firing. V-Loss > 0 → fires when virtual streak reaches limit.",
    howTriggerWorks: "Same trigger options as Stable: Digit Trigger or V-Loss. Burst limit caps consecutive trades.",
    howMartingaleWorks: "Same martingale engine as Stable. On loss: stake escalates. On win: resets.",
    configFields: [
      { name: "Contract Type", desc: "Any type. DIGITEVEN/ODD recommended for 50% win rate." },
      { name: "Pred 1 / 2 / 3", desc: "Cycled through in order on every trade." },
      { name: "Stake", desc: `Keep extremely small. $${MINIMUM_STAKE}–$${(MINIMUM_STAKE * 2).toFixed(2)} recommended.` },
      { name: "V-Loss Limit", desc: "Set 0 for pure every-tick mode. Set 3–5 for triggered mode." },
      { name: "Tick Limit (Burst)", desc: "Maximum ticks before auto-stop. 0 = unlimited." },
    ],
    tips: [
      `Minimum stake $${MINIMUM_STAKE} is perfect for aggressive mode — small stakes, high frequency.`,
      "V-Loss = 0 + DIGITEVEN + tiny stake is the safest aggressive configuration.",
      "Never set martingale > 1.5 in aggressive mode. Escalation happens too fast.",
    ],
  },
  {
    id: "SEQUENCE",
    label: "SEQUENCE",
    emoji: "🔄",
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.22)",
    riskLevel: "MEDIUM",
    contracts: ["Type 1: any", "Type 2: any"],
    triggerType: "Digit match OR V-Loss (per type)",
    vLossUsed: true,
    martingaleUsed: true,
    summary: "Sequence mode runs two independently configured contract types in rotation. Switches after set number of runs.",
    howVLossWorks: "Two SEPARATE V-Loss counters: one for Type 1, one for Type 2. Each increments when its type would have lost.",
    howTriggerWorks: "Each type has its own trigger method: Digit Trigger (comma-separated digits) or V-Loss (streak count).",
    howMartingaleWorks: "Shared martingale counter across both types. Loss on either escalates stake for next trade.",
    configFields: [
      { name: "Type 1/2 Contract", desc: "First and second contract types." },
      { name: "Type 1/2 Triggers", desc: "Comma-separated trigger digits." },
      { name: "Type 1/2 V-Loss", desc: "Virtual losses before trade fires." },
      { name: "Runs Per Type", desc: "How many trades before switching." },
      { name: "Martingale", desc: "Shared multiplier across both types." },
    ],
    tips: [
      "Default config: EVEN triggers [0,2,4] / ODD triggers [1,3,5] works well.",
      "Set Runs Per Type = 3–5. Too low means switching before patterns develop.",
      `Stake minimum $${MINIMUM_STAKE} per trade. Total capital should support at least 5 trades.`,
    ],
  },
  {
    id: "DUAL",
    label: "DUAL",
    emoji: "🎯",
    color: "#10B981",
    bg: "rgba(16,185,129,0.08)",
    border: "rgba(16,185,129,0.22)",
    riskLevel: "LOW–MEDIUM",
    contracts: ["Type 1: any", "Type 2: any (recovery)"],
    triggerType: "Digit match → switches to recovery mode",
    vLossUsed: false,
    martingaleUsed: true,
    summary: "Dual mode is a two-phase system. Phase 1 watches for a trigger digit. When seen, it fires Type 1 and enters recovery mode.",
    howVLossWorks: "Dual mode does NOT use V-Loss. Trigger is purely digit-based.",
    howTriggerWorks: "Phase 1: checks if lastDigit in trigger list. If yes: fires Type 1, enters recovery. Phase 2: every tick fires Type 2 until win.",
    howMartingaleWorks: "Type 2 recovery trades escalate on loss. Type 1 always uses base stake.",
    configFields: [
      { name: "Type 1 Contract", desc: "Initial trigger contract." },
      { name: "Triggers", desc: "Comma-separated digits that trigger Phase 1." },
      { name: "Type 2 Contract", desc: "Recovery contract fired during Phase 2." },
      { name: "Dual Martingale", desc: "Enables/disables martingale for recovery trades." },
    ],
    tips: [
      "Triggers '8,9' with Type 2 = DIGITUNDER 5 is a classic reversion setup.",
      "Recovery mode fires EVERY tick until a Type 2 win.",
      `Keep base stake at $${MINIMUM_STAKE} or slightly higher.`,
    ],
  },
  {
    id: "OVER_UNDER_HEDGE",
    label: "HEDGE",
    emoji: "🔀",
    color: "#7C3AED",
    bg: "rgba(124,58,237,0.08)",
    border: "rgba(124,58,237,0.22)",
    riskLevel: "MEDIUM",
    contracts: ["DIGITOVER 5 (fixed)", "DIGITUNDER 4 (fixed)"],
    triggerType: "Digit match (hedgeTriggers list)",
    vLossUsed: false,
    martingaleUsed: true,
    summary: "Hedge mode fires two trades simultaneously: DIGITOVER 5 AND DIGITUNDER 4 when trigger digit appears.",
    howVLossWorks: "Hedge mode does NOT use V-Loss. Only digit match triggers.",
    howTriggerWorks: "Every tick checks if digit in hedgeTriggers. If yes AND no pending hedge: fires both contracts via Promise.all.",
    howMartingaleWorks: "When both contracts lose (digit 4 or 5), both stakes double next time. Win on either side resets.",
    configFields: [
      { name: "Hedge Stake", desc: `Stake on EACH contract. Minimum $${MINIMUM_STAKE} each.` },
      { name: "Hedge Triggers", desc: "Comma-separated digits that trigger the hedge pair." },
      { name: "Hedge Martingale", desc: "Multiplier applied to BOTH stakes after full loss." },
    ],
    tips: [
      `Total cost per trigger = $${(MINIMUM_STAKE * 2).toFixed(2)} minimum.`,
      "Default triggers are '4,5' — the gap digits. Change to '7,8,9' or '0,1,2' for better results.",
      "Set Stop Loss at least 3× normal setting — gap losses are 2× expensive.",
    ],
  },
  {
    id: "STRIKE",
    label: "STRIKE",
    emoji: "⚡",
    color: "#ff3d3d",
    bg: "rgba(255,61,61,0.08)",
    border: "rgba(255,61,61,0.22)",
    riskLevel: "MEDIUM",
    contracts: ["Main: any", "Recovery: any"],
    triggerType: "Digit match OR V-Loss → enters RUN phase",
    vLossUsed: true,
    martingaleUsed: true,
    summary: "Strike mode has HUNT phase (wait for entry) and RUN phase (auto-trade after entry).",
    howVLossWorks: "In HUNT phase, V-Loss counts virtual losses until entry trigger. In RUN phase, trades fire continuously.",
    howTriggerWorks: "HUNT watches for entry trigger (digit match OR V-Loss streak). When triggered, enters RUN and auto-trades.",
    howMartingaleWorks: "In RUN phase, loss escalates stake. Win returns to main contract and resets stake.",
    configFields: [
      { name: "Main Contract", desc: "Primary contract for RUN phase." },
      { name: "Entry Trigger Method", desc: "DIGIT TRIGGER or V-LOSS for entry." },
      { name: "Entry Triggers", desc: "Digits that trigger entry (if DIGIT TRIGGER)." },
      { name: "Entry V-Loss", desc: "V-Loss streak needed for entry." },
      { name: "Recovery Contract", desc: "Contract used after loss in RUN phase." },
    ],
    tips: [
      "Use digit triggers on 8 or 9 for high-digit entry, then run with UNDER contracts.",
      `Start with stake $${MINIMUM_STAKE} and martingale 1.5x.`,
      "Set Stop Loss to protect against bad entry timing.",
    ],
  },
  {
    id: "PARALLEL",
    label: "PARALLEL",
    emoji: "🔀",
    color: "#e040fb",
    bg: "rgba(224,64,251,0.08)",
    border: "rgba(224,64,251,0.22)",
    riskLevel: "MEDIUM–HIGH",
    contracts: ["Up to 9 different contracts simultaneously"],
    triggerType: "Digit match OR V-Loss (batch trigger)",
    vLossUsed: true,
    martingaleUsed: true,
    summary: "Parallel mode fires multiple trades simultaneously on a single trigger condition. Configure up to 9 different contracts.",
    howVLossWorks: "Uses V-Loss on first trade type. When limit reached, entire batch triggers.",
    howTriggerWorks: "Choose DIGIT TRIGGER (fire on specific digits) or V-LOSS (fire after streak). All trades fire simultaneously.",
    howMartingaleWorks: "If total batch profit is negative, next batch's stake multiplied. Winning batch resets stake.",
    configFields: [
      { name: "Number of Trades", desc: "1-9 trades to fire simultaneously." },
      { name: "Trade Configurations", desc: "Each trade slot has its own contract and prediction." },
      { name: "Trigger Method", desc: "DIGIT TRIGGER or V-LOSS for batch trigger." },
      { name: "Run Mode", desc: "SINGLE = each batch needs trigger. AUTO-RUN = continuous after first." },
    ],
    tips: [
      `Total stake = trades × $${MINIMUM_STAKE} minimum. Plan capital accordingly.`,
      "Cover different digit ranges: OVER 5 + UNDER 4 + EVEN + ODD covers most outcomes.",
      "Start with 2-3 trades per batch to manage risk.",
    ],
  },
  {
    id: "MANUAL",
    label: "MANUAL",
    emoji: "👆",
    color: "#FF9500",
    bg: "rgba(255,149,0,0.08)",
    border: "rgba(255,149,0,0.22)",
    riskLevel: "VARIES",
    contracts: ["Any type — you choose each trade"],
    triggerType: "You click START",
    vLossUsed: false,
    martingaleUsed: false,
    summary: "Manual mode places exactly ONE trade per click. No automation. You decide when and what to trade.",
    howVLossWorks: "No V-Loss. Manual mode has no automated tick monitoring.",
    howTriggerWorks: "You press START. One trade fires immediately.",
    howMartingaleWorks: "No martingale. Stake is exactly what you input.",
    configFields: [
      { name: "Contract Type", desc: "Select any of the 8 contract types." },
      { name: "Stake", desc: `Exact dollar amount. Minimum $${MINIMUM_STAKE}.` },
      { name: "Pred (barrier)", desc: "Your prediction digit." },
      { name: "Ticks", desc: "Duration in ticks for this trade." },
      { name: "Market", desc: "Which volatility index to trade on." },
    ],
    tips: [
      `Minimum trade amount: $${MINIMUM_STAKE}. Always respect this.`,
      "Watch the digit stream for 30+ seconds before your first trade. EVEN = GREEN, ODD = RED.",
      "Use the Digit Circle: if digit 7 has a large bright segment, consider DIGITUNDER 7.",
    ],
  },
];

// ============================================================
// STEP-BY-STEP TRADING PROCESS
// ============================================================

const TRADING_PROCESS = [
  { step: "1. Open Deriv Bot", desc: "Log into your Deriv account and open the Deriv Bot platform. Make sure you're on the DEMO account for testing." },
  { step: "2. Load Nyanyuki Pro", desc: "Find Nyanyuki Pro in the list of available bots. Click to load it. The interface will appear with the digit stream on the left and controls on the right." },
  { step: "3. Select Market", desc: "In the MARKET dropdown, choose a volatility index. For beginners: 1HZ100V (Volatility 100 1s) is recommended — 1 tick per second, easy to follow." },
  { step: "4. Choose Bot Mode", desc: "Click on one of the 8 mode tabs: STABLE, AGGRESSIVE, SEQUENCE, DUAL, HEDGE, STRIKE, PARALLEL, or MANUAL. Each mode has different behavior." },
  { step: "5. Configure Settings", desc: "Fill in the configuration fields for your chosen mode. Pay special attention to Stake (minimum $0.35), Predictions, and V-Loss settings." },
  { step: "6. Set Risk Controls", desc: "Always set Take Profit and Stop Loss in dollars. These are your safety nets. Example: Take Profit $10, Stop Loss $20." },
  { step: "7. Observe the Digit Stream", desc: "Watch the left panel. Digits are color-coded: GREEN for EVEN (0,2,4,6,8), RED for ODD (1,3,5,7,9). The latest tick has a thicker border." },
  { step: "8. Click START", desc: "Press the green START button. The bot begins monitoring. In Stable mode, you'll see the V-Loss counter increment as it watches." },
  { step: "9. Monitor the Journal", desc: "Watch the trade journal at the bottom. It shows every trade with result and P&L. Colour-coded: green = win, red = loss." },
  { step: "10. Review and Reset", desc: "When Take Profit or Stop Loss is hit, the bot stops. Review the journal. Click RESET to clear counters and prepare for next session." },
];

// ============================================================
// DIGIT TRIGGER EXPLANATION
// ============================================================

const DIGIT_TRIGGER_DEEP = {
  title: "Digit Triggers: Complete Technical Explanation",
  intro: "Digit Triggers are the simplest trigger mechanism in Nyanyuki Pro. You specify one or more digits (0–9) as triggers. Whenever the current tick's last digit matches any of your trigger digits, the bot immediately fires a trade (or batch of trades).",
  howItWorks: `The digit trigger logic from the source code:

For each incoming tick:
1. Get the last digit of the tick price
2. Check if this digit appears in your trigger list (e.g., [8,9])
3. If match found AND no trade is currently pending (isTradeTriggered = false):
   - Fire trade(s)
   - Set isTradeTriggered = true to prevent multiple triggers
   - Wait for trade to complete
   - Reset isTradeTriggered = false

For Parallel mode: triggers entire batch at once
For Dual mode: triggers Type 1 and enters recovery phase
For Hedge mode: triggers both OVER and UNDER contracts
For Strike mode: triggers entry into RUN phase`,
  whereUsed: [
    { mode: "STABLE", how: "Optional trigger method. Choose DIGIT TRIGGER in Trigger Mode dropdown." },
    { mode: "AGGRESSIVE", how: "Optional trigger method. Choose DIGIT TRIGGER for controlled bursts." },
    { mode: "SEQUENCE", how: "Each type has its own digit triggers. Type 1 and Type 2 can have different trigger lists." },
    { mode: "DUAL", how: "Primary trigger mechanism. Digit triggers Phase 1 trade." },
    { mode: "HEDGE", how: "Primary trigger mechanism. Digit triggers both contracts." },
    { mode: "STRIKE", how: "Optional entry trigger. Digit triggers RUN phase entry." },
    { mode: "PARALLEL", how: "Optional batch trigger. Digit triggers all parallel trades." },
    { mode: "MANUAL", how: "No digit triggers — you click manually." },
  ],
  bestPractices: [
    "Use 1–3 trigger digits for controlled frequency. More digits = more triggers.",
    "For Dual mode, use rare digits (0 or 9) for less frequent but more deliberate trades.",
    "For Hedge mode, avoid using the gap digits (4,5) as triggers — they cause both contracts to lose.",
    "For Parallel mode, use triggers that appear frequently enough to keep batches flowing, but not too frequent to drain capital.",
    "Combine with V-Loss for two-layer trigger logic: fire after specific digit appears OR after streak of losses.",
  ],
};

const VLOSS_PER_CONTRACT = CONTRACT_TYPES.map(ct => ({
  contract: ct.label,
  condition: ct.vLossCondition,
  example: ct.vLossExample,
  color: ct.color
}));

// ============================================================
// V-LOSS PER CONTRACT TYPE DETAILED EXPLANATION
// ============================================================

const VLOSS_DEEP = {
  title: "V-Loss: Complete Technical Explanation",
  intro: "V-Loss (Volatile Loss) is a virtual loss counter that counts how many times a trade WOULD have lost — without actually placing real money. This allows the bot to wait for a streak to develop before committing funds.",
  howCounterWorks: `The V-Loss counter (virtualStreak.current) works as follows:

On each incoming tick, the bot evaluates whether the current tick's digit (or price direction) represents a loss condition for the configured contract type. The evaluation logic from the source code:`,
  perContract: VLOSS_PER_CONTRACT,
  firesOrStops: "In Stable and Aggressive modes, reaching V-Loss does NOT stop the bot — it TRIGGERS a real trade. Only Take Profit and Stop Loss stop the bot.",
  exampleEVEN: `Example with DIGITEVEN, V-Loss = 3:

Tick 1: digit = 3 (ODD) → V-Loss = 1
Tick 2: digit = 7 (ODD) → V-Loss = 2
Tick 3: digit = 9 (ODD) → V-Loss = 3 → TRADE FIRES on next tick
[Tick 4 result: digit = 4 (EVEN) → WIN → counter resets to 0]`,
  vLossZero: "V-Loss = 0 is a special case: the bot enters 'every tick mode'. It fires a real trade on literally every single incoming tick with no virtual filtering.",
  perMode: [
    { mode: "STABLE", uses: "Yes — primary trigger", detail: "V-Loss is the core trigger. Set 3–6 for DIGITMATCH." },
    { mode: "AGGRESSIVE", uses: "Yes — same as Stable", detail: "V-Loss = 0 is the most common Aggressive setting." },
    { mode: "SEQUENCE", uses: "Yes — per-type triggers", detail: "Type 1 and Type 2 each have their own V-Loss limit." },
    { mode: "DUAL", uses: "No", detail: "Dual uses digit match triggers only." },
    { mode: "HEDGE", uses: "No", detail: "Hedge uses digit match triggers only." },
    { mode: "STRIKE", uses: "Yes — in HUNT phase", detail: "V-Loss triggers entry into RUN phase." },
    { mode: "PARALLEL", uses: "Yes — batch trigger", detail: "V-Loss triggers entire batch when limit reached." },
    { mode: "MANUAL", uses: "No", detail: "Manual is completely manual." },
  ],
};

const TRIGGERS_DEEP = {
  title: "Triggers: How Every Mode Decides to Trade",
  intro: "A trigger is the condition that causes the bot to place a real-money trade. Different modes use fundamentally different trigger types.",
  types: [
    {
      name: "V-Loss Trigger (Stable & Aggressive)",
      color: "#007AFF",
      how: "The virtual streak counter reaches the configured vLossLimit.",
      timing: "Fires on the tick that pushes the counter to the limit.",
      lock: "isTradeTriggered prevents double-firing.",
    },
    {
      name: "Digit Trigger (All modes except Manual)",
      color: "#10B981",
      how: "The tick's last digit appears in the configured trigger list.",
      timing: "Fires on the tick WHERE the trigger digit appears.",
      lock: "triggerLocked prevents re-firing while trade is in-flight.",
    },
    {
      name: "Every-Tick Trigger (V-Loss = 0)",
      color: "#ff3d3d",
      how: "V-Loss = 0 bypasses streak counter entirely.",
      timing: "Fires on every single incoming tick.",
      lock: "Same isTradeTriggered lock applies.",
    },
    {
      name: "Recovery Phase Trigger (Dual mode)",
      color: "#10B981",
      how: "Once Dual enters recovery mode, EVERY tick fires a Type 2 recovery trade.",
      timing: "Fires on every tick during recovery phase.",
      lock: "dualTradeLocked prevents simultaneous Type 2 trades.",
    },
    {
      name: "Strike Entry Trigger (Strike mode)",
      color: "#ff3d3d",
      how: "HUNT phase watches for entry condition (digit match or V-Loss streak).",
      timing: "When entry condition met, enters RUN phase.",
      lock: "strikeTradeLocked prevents multiple entries.",
    },
    {
      name: "Parallel Batch Trigger (Parallel mode)",
      color: "#e040fb",
      how: "When trigger condition met, entire batch fires simultaneously.",
      timing: "All trades fire at once using Promise.all.",
      lock: "parallelPending prevents overlapping batches.",
    },
  ],
};

const MARKETS = [
  { value: "R_10", label: "Volatility 10 Index", speed: "Slow", desc: "Lowest volatility. Best for Manual analysis." },
  { value: "R_25", label: "Volatility 25 Index", speed: "Slow–Med", desc: "Good for EVEN/ODD strategies." },
  { value: "R_50", label: "Volatility 50 Index", speed: "Medium", desc: "Moderate volatility. Balanced digit distribution." },
  { value: "R_75", label: "Volatility 75 Index", speed: "Med–Fast", desc: "Higher volatility. Faster ticks." },
  { value: "R_100", label: "Volatility 100 Index", speed: "Fast", desc: "High volatility. Good for Stable and Sequence." },
  { value: "1HZ10V", label: "Volatility 10 (1s) Index", speed: "1 tick/sec", desc: "1 tick per second. Predictable for digit analysis." },
  { value: "1HZ25V", label: "Volatility 25 (1s) Index", speed: "1 tick/sec", desc: "Good for Hedge mode — clusters at extremes." },
  { value: "1HZ50V", label: "Volatility 50 (1s) Index", speed: "1 tick/sec", desc: "Versatile — works with most modes." },
  { value: "1HZ75V", label: "Volatility 75 (1s) Index", speed: "1 tick/sec", desc: "Recommended for Dual and Sequence." },
  { value: "1HZ100V", label: "Volatility 100 (1s) Index", speed: "1 tick/sec", desc: "Default market. Best for Stable, Aggressive." },
  { value: "1HZ15V", label: "Volatility 15 (1s) Index", speed: "1 tick/sec", desc: "Low-volatility 1s variant." },
  { value: "1HZ30V", label: "Volatility 30 (1s) Index", speed: "1 tick/sec", desc: "Mid-low volatility 1s variant." },
  { value: "1HZ90V", label: "Volatility 90 (1s) Index", speed: "1 tick/sec", desc: "High volatility 1s variant." },
];

const UI_GUIDE = [
  { icon: "🔢", name: "Tick Stream (left panel)", where: "Far left column", what: "Scrolling feed of last digits. Digits coloured GREEN (even: 0,2,4,6,8) or RED (odd: 1,3,5,7,9). The latest tick has a thicker border. Hedge mode trigger digits show a purple dot." },
  { icon: "⭕", name: "Digit Board (5×2 grid)", where: "Centre of main panel", what: "10 cells showing digits 0–9 with frequency percentages. Colour-coded: green = hottest digit, red = coldest digit. Last digit has a glowing border matching its even/odd color." },
  { icon: "💲", name: "Price Panel (dark box)", where: "Top of right panel", what: "Shows live tick price with last digit highlighted. For RISE/FALL, shows ▲/▼ direction and consecutive UP/DOWN counters. Mode status shows V-Loss counter, prediction, martingale level." },
  { icon: "🎛️", name: "Mode Tabs (8 buttons)", where: "Top of right panel", what: "STABLE (blue), AGGRESSIVE (red), SEQUENCE (amber), DUAL (green), HEDGE (purple), STRIKE (red), PARALLEL (purple), MANUAL (orange). Always stop bot before switching." },
  { icon: "📋", name: "Configuration Panel", where: "Main right panel", what: "All settings for active mode. Fields change per mode. Greyed-out fields are locked while bot runs." },
  { icon: "📊", name: "Trade Stats Row", where: "Below configuration", what: "Shows Wins, Losses, Rise trades, Fall trades, Rise win rate, Fall win rate, total P/L. Resets with Reset button." },
  { icon: "📒", name: "Trade Journal", where: "Bottom scrollable section", what: "Every trade logged with contract type, stake, prediction, result, P/L. Colour coded: green = win, red = loss, yellow = info." },
  { icon: "▶️ / ⏹️", name: "START / STOP Button", where: "Bottom of right panel", what: "Green = START, Red = STOP. In Manual mode, START fires one trade. STOP cancels in-progress trade." },
  { icon: "🔄", name: "Reset Button", where: "Bottom right", what: "Resets all counters: P/L, wins/losses, martingale, V-Loss, prediction index. Does NOT reset configuration." },
];

const FIRST_SESSION = [
  { n: "01", t: "Create a Deriv Demo Account", d: "Go to deriv.com and register. Choose VIRTUAL/DEMO account. You receive $10,000 virtual USD." },
  { n: "02", t: "Open Nyanyuki Pro in Deriv Bot", d: "Find the Nyanyuki Pro component. Log in with your Deriv credentials when prompted." },
  { n: "03", t: "Select Market", d: "For first session, pick Volatility 100 (1s) Index. 1 tick per second makes it easy to follow." },
  { n: "04", t: "Watch the Digit Board for 60 Seconds", d: "Note which digits appear frequently (hot, green) and which barely appear (cold, red)." },
  { n: "05", t: "Select Stable Mode", d: "Click the STABLE tab. This is the right starting point." },
  { n: "06", t: "Configure Stable Settings", d: `Base Stake: $${MINIMUM_STAKE}. Pred1/2/3: your three coldest digits. Contract Type: DIGITMATCH. V-Loss: 4. Martingale: 2. Take Profit: 10. Stop Loss: 20. Ticks: 1.` },
  { n: "07", t: "Click START", d: "Watch the V-Loss counter increment. When it hits 4, a real trade fires. Watch the journal." },
  { n: "08", t: "Understand What You See", d: "Losses are expected — DIGITMATCH has 10% win rate. Martingale recovers when a win hits." },
  { n: "09", t: "React to V-Loss Pause Events", d: "If martingale escalates to level 4+ (stake $2.80+), consider your Stop Loss distance." },
  { n: "10", t: "Review After Session", d: "After Take Profit or Stop Loss fires: review journal. Count total trades, wins, martingale levels reached." },
];

// ============================================================
// RESPONSIVE HOOKS
// ============================================================

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

// ============================================================
// HOOKS & SMALL COMPONENTS
// ============================================================

function useInView(t = 0.06) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold: t });
    o.observe(el); return () => o.disconnect();
  }, []);
  return [ref, v];
}

function Fade({ children, delay = 0 }) {
  const [ref, v] = useInView();
  return <div ref={ref} style={{ opacity: v ? 1 : 0, transform: v ? "none" : "translateY(22px)", transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>{children}</div>;
}

function SL({ children }) {
  return <div style={styles.sectionLabel}>{children}</div>;
}

function ST({ children }) {
  return <h2 style={styles.sectionTitle}>{children}</h2>;
}

function Badge({ label, color, bg, border }) {
  return <span style={{ ...styles.badge, color, background: bg, border: `1px solid ${border}` }}>{label}</span>;
}

function CodeBlock({ children }) {
  return (
    <div style={styles.codeBlock}>
      {children}
    </div>
  );
}

function Toggle({ label, color, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...styles.toggle, borderColor: open ? color : "rgba(255,255,255,0.07)" }}>
      <button onClick={() => setOpen(o => !o)} style={styles.toggleButton}>
        <span style={{ ...styles.toggleLabel, color: open ? color : "#fff" }}>{label}</span>
        <span style={{ ...styles.toggleIcon, color, transform: open ? "rotate(45deg)" : "none" }}>+</span>
      </button>
      <div style={{ maxHeight: open ? 3000 : 0, overflow: "hidden", transition: "max-height 0.5s ease" }}>
        <div style={styles.toggleContent}>
          <div style={{ marginTop: 18 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function P({ children, dim = false }) {
  return <p style={{ ...styles.paragraph, color: dim ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.68)" }}>{children}</p>;
}

function Li({ children, color = "#00e676" }) {
  return (
    <div style={styles.listItem}>
      <span style={{ ...styles.listBullet, color }}>›</span>
      <p style={styles.listText}>{children}</p>
    </div>
  );
}

// ============================================================
// CONTRACT TYPE CARD
// ============================================================

function ContractCard({ ct, index }) {
  return (
    <Fade delay={index * 40}>
      <Toggle label={`${ct.label} — ${ct.example}`} color={ct.color}>
        <div style={styles.cardGrid}>
          {[
            { k: "Win Rate", v: ct.winRate, c: "#00e676" },
            { k: "Payout", v: ct.payout, c: "#fff" },
            { k: "Wins On", v: ct.winOn, c: "#00e676" },
            { k: "Loses On", v: ct.loseOn, c: "#ff5252" },
          ].map(({ k, v, c }) => (
            <div key={k} style={styles.cardMetric}>
              <div style={styles.cardMetricLabel}>{k}</div>
              <div style={{ ...styles.cardMetricValue, color: c }}>{v}</div>
            </div>
          ))}
        </div>
        <P>{ct.explain}</P>
        <P>{ct.deepDetail}</P>
        <div style={styles.vLossBox}>
          <div style={styles.vLossLabel}>V-Loss Condition (from source code)</div>
          <CodeBlock>{ct.vLossCondition}</CodeBlock>
          <div style={styles.vLossLabel} style={{ marginTop: 8 }}>Example</div>
          <P dim>{ct.vLossExample}</P>
        </div>
        <div style={styles.availableIn}>Available In</div>
        <div style={styles.badgeContainer}>
          {ct.usedIn.map(m => <Badge key={m} label={m} color={ct.color} bg={ct.bg} border={ct.border} />)}
        </div>
      </Toggle>
    </Fade>
  );
}

// ============================================================
// MODE SECTION
// ============================================================

function ModeSection({ mode, index }) {
  const riskColors = { "LOW": "#00e676", "HIGH": "#ff5252", "MEDIUM": "#ffd600", "LOW–MEDIUM": "#80deea", "MEDIUM–HIGH": "#ff9100", "VARIES": "#ff9100" };
  const rc = riskColors[mode.riskLevel] || "#ffd600";

  return (
    <section id={`mode-${mode.id}`} style={styles.modeSection}>
      <div style={styles.modeSectionContainer}>
        <Fade>
          <div style={styles.modeHeader}>
            <div style={{ ...styles.modeEmoji, background: mode.bg, borderColor: mode.border }}>{mode.emoji}</div>
            <div style={styles.modeHeaderContent}>
              <div style={styles.modeTitleRow}>
                <h2 style={{ ...styles.modeTitle, color: mode.color }}>{mode.label}</h2>
                <Badge label={`Risk: ${mode.riskLevel}`} color={rc} bg={`${rc}18`} border={`${rc}44`} />
                <Badge label={`Mode ${index + 1}/8`} color="rgba(255,255,255,0.25)" bg="transparent" border="rgba(255,255,255,0.1)" />
              </div>
              <div style={styles.modeContracts}>
                {mode.contracts.map(c => <Badge key={c} label={c} color={mode.color} bg={mode.bg} border={mode.border} />)}
              </div>
            </div>
          </div>

          <div style={{ ...styles.modeSummary, background: mode.bg, borderColor: mode.border }}>
            <P>{mode.summary}</P>
          </div>
        </Fade>

        <Fade delay={50}>
          <Toggle label={`V-Loss in ${mode.label} mode`} color={mode.color}>
            <div style={styles.vLossStatus}>
              <Badge label={mode.vLossUsed ? "V-Loss ACTIVE" : "V-Loss NOT USED"} color={mode.vLossUsed ? "#00e676" : "#ff5252"} bg={mode.vLossUsed ? "rgba(0,230,118,0.1)" : "rgba(255,82,82,0.1)"} border={mode.vLossUsed ? "rgba(0,230,118,0.3)" : "rgba(255,82,82,0.3)"} />
            </div>
            <P>{mode.howVLossWorks}</P>
          </Toggle>
        </Fade>

        <Fade delay={70}>
          <Toggle label={`Trigger System in ${mode.label}`} color={mode.color}>
            <div style={styles.triggerBadge}>
              <Badge label={`Trigger: ${mode.triggerType}`} color={mode.color} bg={mode.bg} border={mode.border} />
            </div>
            <P>{mode.howTriggerWorks}</P>
          </Toggle>
        </Fade>

        <Fade delay={90}>
          <Toggle label={`Martingale in ${mode.label}`} color={mode.color}>
            <div style={styles.martingaleStatus}>
              <Badge label={mode.martingaleUsed ? "Martingale ACTIVE" : "No Martingale"} color={mode.martingaleUsed ? "#ffd600" : "#94a3b8"} bg={mode.martingaleUsed ? "rgba(255,214,0,0.1)" : "rgba(148,163,184,0.1)"} border={mode.martingaleUsed ? "rgba(255,214,0,0.3)" : "rgba(148,163,184,0.3)"} />
            </div>
            <P>{mode.howMartingaleWorks}</P>
          </Toggle>
        </Fade>

        <Fade delay={110}>
          <Toggle label={`Configuration Fields`} color={mode.color}>
            <div style={styles.configFields}>
              {mode.configFields.map(f => (
                <div key={f.name} style={styles.configField}>
                  <div style={{ ...styles.configFieldName, color: mode.color }}>{f.name}</div>
                  <P>{f.desc}</P>
                </div>
              ))}
            </div>
          </Toggle>
        </Fade>

        <Fade delay={130}>
          <Toggle label="Pro Tips" color={mode.color}>
            {mode.tips.map((t, i) => <Li key={i} color={mode.color}>{t}</Li>)}
          </Toggle>
        </Fade>
      </div>
    </section>
  );
}

// ============================================================
// NYANYUKI PRO INTEGRATION COMPONENTS
// ============================================================

function NyanyukiModeSelector({ selectedMode, onModeSelect }) {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  
  const modes = [
    { id: 'STABLE', label: '🛡️ STABLE', color: '#007AFF' },
    { id: 'AGGRESSIVE', label: '⚡ AGGRESSIVE', color: '#ff3d3d' },
    { id: 'SEQUENCE', label: '🔄 SEQUENCE', color: '#F59E0B' },
    { id: 'DUAL', label: '🎯 DUAL', color: '#10B981' },
    { id: 'HEDGE', label: '🔀 HEDGE', color: '#7C3AED' },
    { id: 'STRIKE', label: '⚡ STRIKE', color: '#ff3d3d' },
    { id: 'PARALLEL', label: '🔀 PARALLEL', color: '#e040fb' },
    { id: 'MANUAL', label: '👆 MANUAL', color: '#FF9500' }
  ];

  return (
    <div style={styles.modeSelector}>
      <div style={styles.modeSelectorHeader}>
        <h3 style={styles.modeSelectorTitle}>🤖 Nyanyuki Pro Modes (8 Modes)</h3>
      </div>
      <div style={{ ...styles.modeGrid, gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(8, 1fr)' }}>
        {modes.map(mode => (
          <button
            key={mode.id}
            onClick={() => onModeSelect(mode.id)}
            style={{
              ...styles.modeButton,
              borderColor: selectedMode === mode.id ? mode.color : 'rgba(255,255,255,0.1)',
              background: selectedMode === mode.id ? `${mode.color}22` : 'rgba(255,255,255,0.02)',
            }}
          >
            <span style={{ color: mode.color, fontSize: isMobile ? 14 : 18, marginBottom: 2 }}>{mode.label.split(' ')[0]}</span>
            <span style={styles.modeLabel}>{isMobile ? mode.label.split(' ')[1]?.slice(0,3) || mode.label.slice(0,3) : mode.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StrategyConfigurator({ mode, capital }) {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  
  const getConfigForMode = () => {
    const baseStake = Math.max(MINIMUM_STAKE, capital * 0.02);
    
    const configs = {
      STABLE: {
        contractType: 'DIGITMATCH',
        predictions: 'Cold digits from board',
        vLoss: '4-6',
        martingale: '2.0',
        stake: `$${baseStake.toFixed(2)}`,
        notes: `Wait for ${Math.floor(baseStake * 10)} virtual losses before entering`
      },
      AGGRESSIVE: {
        contractType: 'DIGITEVEN',
        predictions: 'N/A',
        vLoss: '0 (Every tick)',
        martingale: '1.0 (disabled)',
        stake: `$${baseStake.toFixed(2)}`,
        notes: `Burst limit: 10-20 ticks, then pause. Min stake $${MINIMUM_STAKE}`
      },
      SEQUENCE: {
        contractType: 'EVEN → ODD',
        predictions: 'Type1:0,2,4 Type2:1,3,5',
        vLoss: '2 per type',
        martingale: '1.5',
        stake: `$${(baseStake * 1.5).toFixed(2)}`,
        notes: 'Switch after 2 runs per type'
      },
      DUAL: {
        contractType: 'EVEN → ODD',
        predictions: 'Trigger: 8,9 Recovery: UNDER 5',
        vLoss: 'Not used',
        martingale: '2.0 (recovery only)',
        stake: `$${baseStake.toFixed(2)}`,
        notes: 'Recovery mode fires until win'
      },
      HEDGE: {
        contractType: 'OVER 5 + UNDER 4',
        predictions: 'Fixed barriers',
        vLoss: 'Not used',
        martingale: '2.0 (both stakes)',
        stake: `$${(baseStake * 2).toFixed(2)} total`,
        notes: `Each contract minimum $${MINIMUM_STAKE}`
      },
      STRIKE: {
        contractType: 'MAIN + RECOVERY',
        predictions: 'Main: 8,9 Recovery: UNDER',
        vLoss: '3-4 for entry',
        martingale: '1.5-2.0',
        stake: `$${(baseStake * 1.5).toFixed(2)}`,
        notes: 'HUNT → RUN phase transition'
      },
      PARALLEL: {
        contractType: 'Multiple (2-9 trades)',
        predictions: 'Cover different ranges',
        vLoss: 'Batch trigger',
        martingale: '2.0 (batch level)',
        stake: `$${(baseStake * 3).toFixed(2)} total`,
        notes: `Each trade minimum $${MINIMUM_STAKE}`
      },
      MANUAL: {
        contractType: 'Your choice',
        predictions: 'Based on digit circle',
        vLoss: 'You decide',
        martingale: 'Manual only',
        stake: `$${(baseStake * 2).toFixed(2)}`,
        notes: `Minimum stake $${MINIMUM_STAKE} per trade`
      }
    };
    
    return configs[mode] || configs.STABLE;
  };

  const config = getConfigForMode();

  return (
    <div style={styles.strategyConfig}>
      <h4 style={styles.strategyTitle}>⚙️ {mode} Mode Configuration</h4>
      <div style={{ ...styles.configGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)' }}>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>Contract Type</span>
          <span style={styles.configValue}>{config.contractType}</span>
        </div>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>Predictions</span>
          <span style={styles.configValue}>{config.predictions}</span>
        </div>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>V-Loss Setting</span>
          <span style={styles.configValue}>{config.vLoss}</span>
        </div>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>Martingale</span>
          <span style={styles.configValue}>{config.martingale}</span>
        </div>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>Recommended Stake</span>
          <span style={{...styles.configValue, color: '#00e676', fontWeight: 700}}>{config.stake}</span>
        </div>
        <div style={styles.configItem}>
          <span style={styles.configLabel}>Notes</span>
          <span style={styles.configValue}>{config.notes}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CHALLENGE TABLE COMPONENT
// ============================================================

function ChallengeTable({ tableData = [], dailyPercentage, result, formData }) {
  const { width } = useWindowSize();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = width < 768 ? 5 : 10;
  
  const safeTableData = Array.isArray(tableData) ? tableData : [];
  const totalPages = Math.ceil(safeTableData.length / rowsPerPage) || 1;
  
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = safeTableData.slice(startIndex, endIndex);

  if (safeTableData.length === 0) {
    return (
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>📊 Challenge Progress Table</h3>
          <div style={styles.tableBadge}>Daily Target: {dailyPercentage}%</div>
        </div>
        <div style={styles.emptyTable}>
          No data available. Please calculate your challenge first.
        </div>
      </div>
    );
  }

  if (width < 768) {
    return (
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>📊 Challenge Progress</h3>
          <div style={styles.tableBadge}>{dailyPercentage}% daily</div>
        </div>
        
        <div style={styles.mobileCards}>
          {currentRows.map((row, idx) => (
            <div key={idx} style={styles.mobileCard}>
              <div style={styles.mobileCardHeader}>
                <span style={styles.mobileCardDay}>Day {row?.day || idx + 1}</span>
                <span style={styles.mobileCardProfit}>{row?.runningProfit || '—'}</span>
              </div>
              <div style={styles.mobileCardContent}>
                <div style={styles.mobileCardRow}>
                  <span>Start: {row?.startCapital || '—'}</span>
                  <span>End: {row?.endCapital || '—'}</span>
                </div>
                <div style={styles.mobileCardRow}>
                  <span>Daily: {row?.dailyTarget || '—'}</span>
                  <span>Profit: {row?.profit || '—'}</span>
                </div>
                <div style={styles.mobileCardSessions}>
                  <span>S1: {row?.session1 || '—'}</span>
                  <span>S2: {row?.session2 || '—'}</span>
                  <span>S3: {row?.session3 || '—'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={styles.pageButton}>←</button>
            <span style={styles.pageInfo}>{currentPage} / {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={styles.pageButton}>→</button>
          </div>
        )}

        <div style={styles.exportSection}>
          <button onClick={() => generatePDF(result, formData)} style={styles.pdfButton}>📥 PDF</button>
          <button onClick={() => {
            const csvContent = [
              ['Day', 'Start Capital', 'Daily %', 'Session 1', 'Session 2', 'Session 3', 'Daily Target', 'Daily Profit', 'End Capital', 'Running Profit'],
              ...safeTableData.map(row => [
                row?.day || '', row?.startCapital || '', row?.dailyPercentage || '',
                row?.session1 || '', row?.session2 || '', row?.session3 || '',
                row?.dailyTarget || '', row?.profit || '', row?.endCapital || '', row?.runningProfit || ''
              ])
            ].map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `challenge-table-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
          }} style={styles.exportButton}>📄 CSV</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.tableContainer}>
      <div style={styles.tableHeader}>
        <h3 style={styles.tableTitle}>📊 Challenge Progress Table (Compound Interest)</h3>
        <div style={styles.tableBadge}>Daily Target: {dailyPercentage}%</div>
        <div style={styles.tableBadge} style={{ background: '#ff525210', borderColor: '#ff525244', color: '#ff5252' }}>Min Stake: ${MINIMUM_STAKE}</div>
      </div>
      
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeadRow}>
              <th style={styles.tableHead}>Day</th>
              <th style={styles.tableHead}>Start</th>
              <th style={styles.tableHead}>%</th>
              <th style={styles.tableHead}>S1</th>
              <th style={styles.tableHead}>S2</th>
              <th style={styles.tableHead}>S3</th>
              <th style={styles.tableHead}>Daily $</th>
              <th style={styles.tableHead}>Profit</th>
              <th style={styles.tableHead}>End</th>
              <th style={styles.tableHead}>Total</th>
             </tr>
          </thead>
          <tbody>
            {currentRows.map((row, idx) => (
              <tr key={idx} style={{ ...styles.tableRow, background: row && row.profit !== '—' ? 'rgba(0,230,118,0.02)' : 'transparent' }}>
                <td style={styles.tableCell}><span style={styles.dayNumber}>Day {row?.day || idx + 1}</span></td>
                <td style={styles.tableCell}><span style={styles.capitalValue}>{row?.startCapital || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.percentageValue}>{row?.dailyPercentage || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.sessionTarget}>{row?.session1 || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.sessionTarget}>{row?.session2 || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.sessionTarget}>{row?.session3 || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.dailyTarget}>{row?.dailyTarget || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.profitValue}>{row?.profit || '—'}</span></td>
                <td style={styles.tableCell}><span style={styles.capitalValue}>{row?.endCapital || '—'}</span></td>
                <td style={styles.tableCell}>
                  {row?.runningProfit && row.runningProfit !== '—' ? (
                    <span style={{ ...styles.profitValue, color: parseFloat(row.runningProfit.replace('$', '')) > 0 ? '#00e676' : '#fff' }}>
                      {row.runningProfit}
                    </span>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={styles.pageButton}>←</button>
          <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={styles.pageButton}>→</button>
        </div>
      )}

      <div style={styles.exportSection}>
        <button onClick={() => generatePDF(result, formData)} style={styles.pdfButton}>📥 Download PDF</button>
        <button onClick={() => {
          const csvContent = [
            ['Day', 'Start Capital', 'Daily %', 'Session 1', 'Session 2', 'Session 3', 'Daily Target', 'Daily Profit', 'End Capital', 'Running Profit'],
            ...safeTableData.map(row => [
              row?.day || '', row?.startCapital || '', row?.dailyPercentage || '',
              row?.session1 || '', row?.session2 || '', row?.session3 || '',
              row?.dailyTarget || '', row?.profit || '', row?.endCapital || '', row?.runningProfit || ''
            ])
          ].map(row => row.join(',')).join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `challenge-table-${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
        }} style={styles.exportButton}>📄 Export CSV</button>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================

export default function NyanyukiProComplete() {
  const { width } = useWindowSize();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('guide');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    startCapital: '',
    dailyPercentage: '5',
    customDays: ''
  });
  const [calculationResult, setCalculationResult] = useState(null);
  const [selectedMode, setSelectedMode] = useState('STABLE');
  const [errors, setErrors] = useState({});

  const isMobile = width < 768;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('nyanyukiChallengeData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed.formData || formData);
        setCalculationResult(parsed.result || null);
        setSelectedMode(parsed.selectedMode || 'STABLE');
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (formData.startCapital || calculationResult) {
      localStorage.setItem('nyanyukiChallengeData', JSON.stringify({
        formData,
        result: calculationResult,
        selectedMode
      }));
    }
  }, [formData, calculationResult, selectedMode]);

  const go = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!formData.startCapital || formData.startCapital < 1) newErrors.startCapital = 'Required';
    if (!formData.dailyPercentage || formData.dailyPercentage < 0.1) newErrors.dailyPercentage = 'Required';
    if (formData.dailyPercentage > 100) newErrors.dailyPercentage = 'Max 100%';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCalculate = () => {
    if (!validateInputs()) return;
    const calculator = new ChallengeCalculator(formData.startCapital, formData.dailyPercentage, formData.customDays);
    const result = calculator.calculate();
    setCalculationResult(result);
    setActiveTab('challenge');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
  };

  const sections = [
    { id: "contracts", label: "Types" },
    { id: "vloss", label: "V-Loss" },
    { id: "triggers", label: "Triggers" },
    { id: "digit-triggers", label: "Digit Triggers" },
    { id: "process", label: "Process" },
    { id: "mode-STABLE", label: "Stable" },
    { id: "mode-AGGRESSIVE", label: "Aggressive" },
    { id: "mode-SEQUENCE", label: "Sequence" },
    { id: "mode-DUAL", label: "Dual" },
    { id: "mode-OVER_UNDER_HEDGE", label: "Hedge" },
    { id: "mode-STRIKE", label: "Strike" },
    { id: "mode-PARALLEL", label: "Parallel" },
    { id: "mode-MANUAL", label: "Manual" },
    { id: "ui", label: "UI" },
    { id: "start", label: "Start" },
  ];

  const css = `
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    html{scroll-behavior:smooth}
    body{background:#07090d;color:#fff;overflow-x:hidden;font-family:'DM Sans',sans-serif}
    @media (max-width: 768px){html{font-size:14px}}
    ::-webkit-scrollbar{width:3px}
    ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px}
    @keyframes up{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:none}}
    @keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}}
  `;

  return (
    <>
      <style>{css}</style>

      <nav style={{ ...styles.nav, background: scrolled ? "rgba(7,9,13,0.95)" : "transparent", backdropFilter: scrolled ? "blur(20px)" : "none" }}>
        <button onClick={() => go("top")} style={styles.logo}>NYA <span style={styles.logoLight}>PRO</span></button>
        
        {isMobile ? (
          <>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={styles.menuButton}>☰</button>
            {mobileMenuOpen && (
              <div style={styles.mobileMenu}>
                <button onClick={() => { setActiveTab('guide'); setMobileMenuOpen(false); }} style={styles.mobileMenuItem}>📖 Guide</button>
                <button onClick={() => { setActiveTab('challenge'); setMobileMenuOpen(false); }} style={styles.mobileMenuItem}>🎯 Challenge</button>
                <div style={styles.mobileMenuDivider} />
                {activeTab === 'guide' && sections.map(s => (
                  <button key={s.id} onClick={() => go(s.id)} style={styles.mobileMenuItem}>{s.label}</button>
                ))}
                <div style={styles.mobileMenuDivider} />
                <button onClick={() => window.open("https://nyanyuki.site", "_blank")} style={styles.mobileMenuItem}>App ↗</button>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={styles.navTabs}>
              <button onClick={() => setActiveTab('guide')} style={{ ...styles.navTab, background: activeTab === 'guide' ? '#00e676' : 'transparent', color: activeTab === 'guide' ? '#000' : 'rgba(255,255,255,0.5)', borderColor: activeTab === 'guide' ? '#00e676' : 'rgba(255,255,255,0.1)' }}>📖 Guide</button>
              <button onClick={() => setActiveTab('challenge')} style={{ ...styles.navTab, background: activeTab === 'challenge' ? '#00e676' : 'transparent', color: activeTab === 'challenge' ? '#000' : 'rgba(255,255,255,0.5)', borderColor: activeTab === 'challenge' ? '#00e676' : 'rgba(255,255,255,0.1)' }}>🎯 Challenge</button>
            </div>

            {activeTab === 'guide' && (
              <div style={styles.navSections}>
                {sections.map(s => <button key={s.id} className="nb" onClick={() => go(s.id)} style={styles.navSectionButton}>{s.label}</button>)}
                <div style={styles.navDivider} />
                <button className="lb" onClick={() => window.open("https://nyanyuki.site", "_blank")} style={styles.navAppButton}>App ↗</button>
              </div>
            )}
          </>
        )}
      </nav>

      {activeTab === 'guide' && (
        <>
          <div id="top" style={styles.hero}>
            <div style={styles.heroBackground} />
            <div style={styles.heroContent}>
              <div style={styles.heroLabel}>Definitive Operator Guide — Built From Source Code</div>
              <h1 style={styles.heroTitle}>Nyanyuki<br /><span style={styles.heroHighlight}>Pro</span><br />Guide</h1>
              <p style={styles.heroDescription}>Every trade type explained. V-Loss and trigger mechanics sourced directly from the bot's code. All 8 modes with full configuration walkthrough. Plus integrated Challenge Calculator with percentage-based daily targets and compound interest.</p>
              <div style={styles.heroButtons}>
                {[
                  { id: "contracts", label: "8 Trade Types", color: "#00e676" },
                  { id: "vloss", label: "V-Loss Deep Dive", color: "#ffd600" },
                  { id: "triggers", label: "All Triggers", color: "#e040fb" },
                  { id: "digit-triggers", label: "Digit Triggers", color: "#10B981" },
                  { id: "process", label: "Trading Process", color: "#ff9100" },
                ].map(p => (
                  <button key={p.id} onClick={() => go(p.id)} style={{ ...styles.heroButton, color: p.color, borderColor: `${p.color}44` }}>{p.label}</button>
                ))}
              </div>
              <div style={styles.heroActions}>
                <button className="lb-lg" onClick={() => go("start")} style={styles.heroPrimary}>Start Reading →</button>
                <button onClick={() => setActiveTab('challenge')} style={styles.heroSecondary}>Try Challenge MVP →</button>
              </div>
            </div>
          </div>

          <div style={styles.ticker}>
            <div style={styles.tickerContent}>
              {[...Array(2)].map((_, i) => (
                <div key={i} style={styles.tickerItems}>
                  {["OVER", "UNDER", "EVEN", "ODD", "MATCH", "DIFF", "RISE", "FALL", "V-LOSS", "TRIGGERS", "MARTINGALE", "STABLE", "AGGRESSIVE", "SEQUENCE", "DUAL", "HEDGE", "STRIKE", "PARALLEL", "MANUAL", "13 MARKETS", `MIN STAKE $${MINIMUM_STAKE}`].map(t => (
                    <span key={t} style={styles.tickerItem}>▸ {t}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <section style={styles.foundation}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Foundation</SL>
                <ST>What Nyanyuki Pro Is</ST>
                <div style={styles.foundationGrid}>
                  {[
                    { t: "What it is", b: "Nyanyuki Pro is an automated digit trading bot that runs inside Deriv's platform. It connects to your Deriv account via the official Deriv API (WebSocket), watches live tick prices, and places trades based on the mode and configuration you set." },
                    { t: "What 'last digit' means", b: "Every Deriv volatility index produces a new price (tick) every 1–5 seconds. Each price has a last digit: if the price is 2,847.3 the last digit is 3. All digit contracts bet on what the last digit of the NEXT tick will be." },
                    { t: "Strict Color Coding", b: "The digit stream uses strict colors: EVEN digits (0,2,4,6,8) = GREEN (#10B981). ODD digits (1,3,5,7,9) = RED (#EF4444). The latest digit has a matching border. This makes pattern recognition instant." },
                    { t: `Minimum Stake: $${MINIMUM_STAKE}`, b: `Deriv's minimum trade amount is $${MINIMUM_STAKE}. All stakes in the bot must be at least this amount. The Challenge Calculator and recommendations respect this minimum.` },
                  ].map((item, i) => (
                    <Fade key={item.t} delay={i * 50}>
                      <div style={styles.foundationCard}>
                        <div style={styles.foundationCardTitle}>{item.t}</div>
                        <P dim>{item.b}</P>
                      </div>
                    </Fade>
                  ))}
                </div>
              </Fade>
            </div>
          </section>

          <section id="contracts" style={styles.section}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>All Trade Types</SL>
                <ST>8 Contract Types<br />Fully Explained</ST>
                <P dim>Click any contract type to expand the full explanation, win/loss conditions, payout, which modes use it, and the exact V-Loss evaluation code from the source.</P>
              </Fade>
              <div style={styles.contractsGrid}>
                {CONTRACT_TYPES.map((ct, i) => <ContractCard key={ct.id} ct={ct} index={i} />)}
              </div>
            </div>
          </section>

          <section id="vloss" style={styles.sectionAlt}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>V-Loss Mechanics</SL>
                <ST>V-Loss: Complete<br />Technical Guide</ST>
                <div style={styles.vLossIntro}><P>{VLOSS_DEEP.intro}</P></div>
              </Fade>

              <Fade delay={40}>
                <div style={styles.vLossSubtitle}>How the Counter Works — Source Code</div>
                <P>{VLOSS_DEEP.howCounterWorks.split('\n\n')[0]}</P>
                <CodeBlock>{VLOSS_DEEP.howCounterWorks.split('\n\n').slice(1).join('\n\n')}</CodeBlock>
              </Fade>

              <Fade delay={60}>
                <div style={styles.vLossSubtitle}>V-Loss by Contract Type</div>
                <div style={styles.vLossModes}>
                  {VLOSS_DEEP.perContract.map(ct => (
                    <div key={ct.contract} style={{ ...styles.vLossMode, borderLeftColor: ct.color }}>
                      <div style={{ ...styles.vLossModeTitle, color: ct.color }}>{ct.contract}</div>
                      <CodeBlock>{ct.condition}</CodeBlock>
                      <P dim>{ct.example}</P>
                    </div>
                  ))}
                </div>
              </Fade>

              <Fade delay={80}>
                <div style={styles.vLossSubtitle}>V-Loss = 0: Every Tick Mode</div>
                <P>{VLOSS_DEEP.vLossZero}</P>
              </Fade>

              <Fade delay={100}>
                <div style={styles.vLossSubtitle}>V-Loss by Mode</div>
                <div style={styles.vLossModes}>
                  {VLOSS_DEEP.perMode.map(m => (
                    <div key={m.mode} style={styles.vLossMode}>
                      <div style={styles.vLossModeTitle}>{m.mode}</div>
                      <div style={{ ...styles.vLossModeUses, color: m.uses.includes("Yes") ? "#00e676" : "#ff5252" }}>{m.uses}</div>
                      <P dim>{m.detail}</P>
                    </div>
                  ))}
                </div>
              </Fade>
            </div>
          </section>

          <section id="triggers" style={styles.section}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Trigger Mechanics</SL>
                <ST>When Does the Bot<br />Actually Trade?</ST>
                <P dim>{TRIGGERS_DEEP.intro}</P>
              </Fade>
              <div style={styles.triggersList}>
                {TRIGGERS_DEEP.types.map((t, i) => (
                  <Fade key={t.name} delay={i * 50}>
                    <div style={{ ...styles.triggerItem, borderLeftColor: t.color }}>
                      <div style={{ ...styles.triggerName, color: t.color }}>{t.name}</div>
                      <CodeBlock>{t.how}</CodeBlock>
                      <div style={styles.triggerDetails}>
                        <div style={styles.triggerDetail}>
                          <div style={styles.triggerDetailLabel}>Timing</div>
                          <P dim>{t.timing}</P>
                        </div>
                        <div style={styles.triggerDetail}>
                          <div style={styles.triggerDetailLabel}>Locking / Safety</div>
                          <P dim>{t.lock}</P>
                        </div>
                      </div>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </section>

          <section id="digit-triggers" style={styles.sectionAlt}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Digit Triggers</SL>
                <ST>Digit Triggers:<br />Complete Technical Guide</ST>
                <P>{DIGIT_TRIGGER_DEEP.intro}</P>
              </Fade>

              <Fade delay={40}>
                <div style={styles.vLossSubtitle}>How Digit Triggers Work — Source Code</div>
                <CodeBlock>{DIGIT_TRIGGER_DEEP.howItWorks}</CodeBlock>
              </Fade>

              <Fade delay={60}>
                <div style={styles.vLossSubtitle}>Where Digit Triggers Are Used</div>
                <div style={styles.vLossModes}>
                  {DIGIT_TRIGGER_DEEP.whereUsed.map(m => (
                    <div key={m.mode} style={styles.vLossMode}>
                      <div style={styles.vLossModeTitle}>{m.mode}</div>
                      <P dim>{m.how}</P>
                    </div>
                  ))}
                </div>
              </Fade>

              <Fade delay={80}>
                <div style={styles.vLossSubtitle}>Best Practices</div>
                {DIGIT_TRIGGER_DEEP.bestPractices.map((tip, i) => <Li key={i} color="#00e676">{tip}</Li>)}
              </Fade>
            </div>
          </section>

          <section id="process" style={styles.section}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Trading Process</SL>
                <ST>Step-by-Step<br />How to Trade</ST>
                <P dim>Follow these steps to start trading with Nyanyuki Pro:</P>
              </Fade>
              <div style={styles.firstSessionList}>
                <div style={styles.firstSessionTimeline} />
                {TRADING_PROCESS.map((s, i) => (
                  <Fade key={s.step} delay={i * 35}>
                    <div style={styles.firstSessionItem}>
                      <div style={styles.firstSessionNumber}>{s.step}</div>
                      <div style={styles.firstSessionContent}>
                        <div style={styles.firstSessionTitle}>{s.desc}</div>
                        {s.step === "6" && <Badge label={`Min Stake: $${MINIMUM_STAKE}`} color="#ff5252" bg="rgba(255,82,82,0.1)" border="rgba(255,82,82,0.3)" />}
                      </div>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </section>

          {MODES_DATA.map((mode, i) => <ModeSection key={mode.id} mode={mode} index={i} />)}

          <section style={styles.sectionAlt}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Available Markets</SL>
                <ST>All 13 Volatility<br />Markets</ST>
              </Fade>
              <div style={styles.marketsGrid}>
                {MARKETS.map((m, i) => (
                  <Fade key={m.value} delay={i * 30}>
                    <div style={styles.marketCard}>
                      <div style={styles.marketHeader}>
                        <div style={styles.marketTitle}>{m.label}</div>
                        <Badge label={m.speed} color="#00e676" bg="rgba(0,230,118,0.08)" border="rgba(0,230,118,0.2)" />
                      </div>
                      <P dim>{m.desc}</P>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </section>

          <section id="ui" style={styles.section}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Interface Guide</SL>
                <ST>Every Element<br />On Screen</ST>
              </Fade>
              <div style={styles.uiGuideList}>
                {UI_GUIDE.map((el, i) => (
                  <Fade key={el.name} delay={i * 35}>
                    <div style={styles.uiGuideItem}>
                      <div style={styles.uiGuideIcon}>{el.icon}</div>
                      <div style={styles.uiGuideContent}>
                        <div style={styles.uiGuideHeader}>
                          <div style={styles.uiGuideName}>{el.name}</div>
                          <Badge label={el.where} color="rgba(255,255,255,0.2)" bg="transparent" border="rgba(255,255,255,0.1)" />
                        </div>
                        <P dim>{el.what}</P>
                      </div>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </section>

          <section style={styles.sectionAlt}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Martingale Reference</SL>
                <ST>Stake Escalation<br />Full Table (2× multiplier)</ST>
                <div style={styles.tableWrapper}>
                  <table style={styles.martingaleTable}>
                    <thead>
                      <tr style={styles.martingaleHeadRow}>
                        {["Level", "Stake", "Total", "Payout", "Net", "Notes"].map(h => (<th key={h} style={styles.martingaleHead}>{h}</th>))}
                      </tr>
                    </thead>
                    <tbody>
                      {[1,2,3,4,5,6,7,8].map((lvl) => {
                        const stake = Math.pow(2, lvl - 1) * MINIMUM_STAKE;
                        const spent = (Math.pow(2, lvl) - 1) * MINIMUM_STAKE;
                        const payout = stake * 8;
                        const net = payout - spent;
                        return (
                          <tr key={lvl} style={{ ...styles.martingaleRow, background: lvl >= 6 ? "rgba(255,82,82,0.04)" : lvl >= 4 ? "rgba(255,145,0,0.04)" : "transparent" }}>
                            <td style={styles.martingaleCell}>{lvl}</td>
                            <td style={{ ...styles.martingaleCell, color: lvl >= 6 ? "#ff5252" : lvl >= 4 ? "#ff9100" : "#fff", fontWeight: 700 }}>${stake.toFixed(2)}</td>
                            <td style={styles.martingaleCell}>${spent.toFixed(2)}</td>
                            <td style={styles.martingaleCell}>${payout.toFixed(2)}</td>
                            <td style={{ ...styles.martingaleCell, color: net > 0 ? "#00e676" : "#ff5252", fontWeight: 700 }}>+${net.toFixed(2)}</td>
                            <td style={styles.martingaleCell}>{lvl === 1 ? `Base (min $${MINIMUM_STAKE})` : lvl <= 3 ? "Normal" : lvl === 4 ? "⚠ Watch" : lvl === 5 ? "⚠ High" : "🚨 Critical"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <P dim style={styles.martingaleNote}>Table assumes DIGITMATCH (8× payout) from minimum stake $${MINIMUM_STAKE} with 2× multiplier. Actual stakes must be ≥ $${MINIMUM_STAKE}.</P>
              </Fade>
            </div>
          </section>

          <section id="start" style={styles.section}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>Getting Started</SL>
                <ST>Your First Session<br />Step by Step</ST>
              </Fade>
              <div style={styles.firstSessionList}>
                <div style={styles.firstSessionTimeline} />
                {FIRST_SESSION.map((s, i) => (
                  <Fade key={s.n} delay={i * 35}>
                    <div style={styles.firstSessionItem}>
                      <div style={styles.firstSessionNumber}>{s.n}</div>
                      <div style={styles.firstSessionContent}>
                        <div style={styles.firstSessionTitle}>{s.t}</div>
                        <P dim>{s.d}</P>
                      </div>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </section>

          <section style={styles.sectionAlt}>
            <div style={styles.sectionContainer}>
              <Fade>
                <SL>At a Glance</SL>
                <ST>Mode Comparison<br />Table</ST>
                <div style={styles.tableWrapper}>
                  <table style={styles.comparisonTable}>
                    <thead>
                      <tr style={styles.comparisonHeadRow}>
                        {["Mode","Risk","V-Loss","Trigger","Martingale","Best For"].map(h => (<th key={h} style={styles.comparisonHead}>{h}</th>))}
                      </tr>
                    </thead>
                    <tbody>
                      {MODES_DATA.map((m, i) => {
                        const riskColors = { "LOW": "#00e676", "HIGH": "#ff5252", "MEDIUM": "#ffd600", "LOW–MEDIUM": "#80deea", "MEDIUM–HIGH": "#ff9100", "VARIES": "#ff9100" };
                        return (
                          <tr key={m.id} style={styles.comparisonRow}>
                            <td onClick={() => go(`mode-${m.id}`)} style={{ ...styles.comparisonCell, color: m.color, cursor: "pointer" }}>{m.emoji} {m.label}</td>
                            <td style={{ ...styles.comparisonCell, color: riskColors[m.riskLevel] || "#ffd600" }}>{m.riskLevel}</td>
                            <td style={{ ...styles.comparisonCell, color: m.vLossUsed ? "#00e676" : "#ff5252" }}>{m.vLossUsed ? "Yes" : "No"}</td>
                            <td style={styles.comparisonCell}>{m.triggerType}</td>
                            <td style={{ ...styles.comparisonCell, color: m.martingaleUsed ? "#ffd600" : "#94a3b8" }}>{m.martingaleUsed ? "Yes" : "No"}</td>
                            <td style={styles.comparisonCell}>{m.riskLevel === "LOW" ? "Beginners" : m.riskLevel === "HIGH" ? "Advanced" : "Intermediate"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Fade>
            </div>
          </section>

          <section style={styles.cta}>
            <div style={styles.ctaBackground} />
            <Fade>
              <div style={styles.ctaLabel}>You've read the guide</div>
              <h2 style={styles.ctaTitle}>Plan Your Challenge</h2>
              <p style={styles.ctaDescription}>Use the Challenge MVP to calculate your targets with percentage-based daily goals and compound interest.</p>
              <button className="lb-lg" onClick={() => setActiveTab('challenge')} style={styles.ctaButton}>Open Challenge MVP →</button>
            </Fade>
          </section>
        </>
      )}

      {activeTab === 'challenge' && (
        <div style={styles.challengeContainer}>
          <h1 style={styles.challengeTitle}>Trading Challenge <span style={styles.challengeHighlight}>MVP</span></h1>
          <p style={styles.challengeSubtitle}>Plan your 3-session daily trading with percentage-based targets and compound interest</p>

          <div style={styles.calculatorContainer}>
            <h2 style={styles.calculatorTitle}>📊 Enter Your Challenge Details</h2>
            <div style={{ ...styles.formRow, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Starting Capital ($)</label>
                <input type="number" value={formData.startCapital} onChange={(e) => handleInputChange('startCapital', e.target.value)} placeholder="e.g., 100" style={{ ...styles.input, borderColor: errors.startCapital ? '#ff5252' : 'rgba(255,255,255,0.1)' }} />
                {errors.startCapital && <span style={styles.errorText}>{errors.startCapital}</span>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Daily Target (%)</label>
                <input type="number" step="0.1" value={formData.dailyPercentage} onChange={(e) => handleInputChange('dailyPercentage', e.target.value)} placeholder="e.g., 5" style={{ ...styles.input, borderColor: errors.dailyPercentage ? '#ff5252' : 'rgba(255,255,255,0.1)' }} />
                {errors.dailyPercentage && <span style={styles.errorText}>{errors.dailyPercentage}</span>}
              </div>
            </div>

            <div style={{ ...styles.formRow, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Custom Duration (Days) - Optional</label>
                <input type="number" value={formData.customDays} onChange={(e) => handleInputChange('customDays', e.target.value)} placeholder="Leave empty for 30 days" style={styles.input} min="1" max="365" />
                <span style={styles.hint}>Number of days to project</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Recommended Profiles</label>
                <div style={styles.recommendationBox}>
                  {Object.entries(RECOMMENDED_PERCENTAGES).map(([key, profile]) => (
                    <button key={key} onClick={() => handleInputChange('dailyPercentage', profile.range[0])} style={{ ...styles.profileButton, background: profile.bg, borderColor: profile.color }}>
                      <span style={{ color: profile.color, fontWeight: 700 }}>{profile.name}</span>
                      <span style={styles.profileRange}>{profile.range[0]}-{profile.range[1]}%</span>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>min ${MINIMUM_STAKE}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={handleCalculate} style={styles.calculateButton}>Calculate My Challenge →</button>
          </div>

          {calculationResult && (
            <>
              <div style={styles.resultsContainer}>
                <h2 style={styles.resultsTitle}>📈 Your Challenge Plan</h2>
                <div style={styles.metricsGrid}>
                  <div style={styles.metricCard}><span style={styles.metricLabel}>Daily Target</span><span style={styles.metricValue}>{formatCurrency(calculationResult.dailyTarget)}</span></div>
                  <div style={styles.metricCard}><span style={styles.metricLabel}>Per Session</span><span style={styles.metricValue}>{formatCurrency(calculationResult.sessionTarget)}</span></div>
                  <div style={styles.metricCard}><span style={styles.metricLabel}>Duration</span><span style={styles.metricValue}>{calculationResult.daysNeeded} days</span><span style={styles.metricSub}>({calculationResult.weeksNeeded} wks)</span></div>
                  <div style={styles.metricCard}><span style={styles.metricLabel}>Final Capital</span><span style={styles.metricValue}>{formatCurrency(calculationResult.finalCapital)}</span></div>
                </div>
                <div style={styles.progressSection}>
                  <div style={styles.progressHeader}><span>From {formatCurrency(calculationResult.startCapital)} to {formatCurrency(calculationResult.finalCapital)}</span><span>{calculationResult.totalReturn}% return</span></div>
                  <div style={styles.progressBar}><div style={{ ...styles.progressFill, width: `${(calculationResult.startCapital / calculationResult.finalCapital) * 100}%` }} /></div>
                  <div style={styles.totalProfit}>Total Profit: ${calculationResult.totalProfit}</div>
                  <div style={styles.totalProfit} style={{ color: '#ff5252' }}>Minimum Stake: ${MINIMUM_STAKE} per trade on Deriv</div>
                  {calculationResult.recommendedProfile && (
                    <div style={{ ...styles.profileInfo, borderColor: calculationResult.recommendedProfile.color, color: calculationResult.recommendedProfile.color }}>
                      {calculationResult.recommendedProfile.name}: {calculationResult.recommendedProfile.description}
                    </div>
                  )}
                </div>
              </div>

              <ChallengeTable tableData={calculationResult.challengeTable} dailyPercentage={calculationResult.dailyPercentage} result={calculationResult} formData={formData} />

              <div style={styles.integrationContainer}>
                <NyanyukiModeSelector selectedMode={selectedMode} onModeSelect={setSelectedMode} />
                <StrategyConfigurator mode={selectedMode} capital={calculationResult.startCapital} />
              </div>
            </>
          )}
        </div>
      )}

      <footer style={styles.footer}>
        <div style={styles.footerLogo}>NYANYUKI PRO</div>
        <div style={styles.footerText}>Trading involves risk · Always start on demo · Never risk more than you can afford to lose · Min stake ${MINIMUM_STAKE} · {new Date().getFullYear()}</div>
      </footer>
    </>
  );
}

// ============================================================
// RESPONSIVE STYLES (Same as previous version, omitted for brevity but included in actual code)
// ============================================================

const styles = {
  nav: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, height: 54, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", transition: "all 0.3s" },
  logo: { background: "none", border: "none", cursor: "pointer", fontFamily: "'Unbounded',sans-serif", fontSize: 13, fontWeight: 900, color: "#00e676", letterSpacing: 1, whiteSpace: "nowrap" },
  logoLight: { color: "rgba(255,255,255,0.2)", fontWeight: 400 },
  menuButton: { background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", fontSize: 20, padding: "4px 12px", cursor: "pointer" },
  mobileMenu: { position: "absolute", top: 54, left: 0, right: 0, background: "#07090d", borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "10px 0", zIndex: 199 },
  mobileMenuItem: { display: "block", width: "100%", padding: "12px 20px", background: "none", border: "none", color: "rgba(255,255,255,0.7)", textAlign: "left", fontSize: 14, cursor: "pointer" },
  mobileMenuDivider: { height: 1, background: "rgba(255,255,255,0.05)", margin: "8px 0" },
  navTabs: { display: "flex", gap: 10, marginRight: "auto", marginLeft: 20 },
  navTab: { padding: "6px 16px", borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" },
  navSections: { display: "flex", gap: 0, alignItems: "center", flexWrap: "nowrap", overflow: "hidden" },
  navSectionButton: { background: "none", border: "none", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", cursor: "pointer", padding: "6px 10px", transition: "color 0.2s" },
  navAppButton: { background: "none", border: "none", fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1.5, color: "#00e676", cursor: "pointer", padding: "6px 10px" },
  navDivider: { width: 1, height: 16, background: "rgba(255,255,255,0.08)", margin: "0 6px", flexShrink: 0 },
  sectionContainer: { maxWidth: 1200, margin: "0 auto", padding: "0 24px" },
  section: { padding: "80px 0" },
  sectionAlt: { padding: "80px 0", background: "rgba(255,255,255,0.015)", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  sectionLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 },
  sectionTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: "clamp(26px,5vw,46px)", fontWeight: 900, color: "#fff", marginBottom: 36, letterSpacing: -0.5, lineHeight: 1.1 },
  hero: { minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "110px 24px 80px", maxWidth: 1200, margin: "0 auto", position: "relative" },
  heroBackground: { position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.018, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: "200px" },
  heroContent: { position: "relative", zIndex: 1, animation: "up 0.8s ease both" },
  heroLabel: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: "rgba(0,230,118,0.7)", letterSpacing: 3, marginBottom: 22, textTransform: "uppercase" },
  heroTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: "clamp(44px,9vw,92px)", fontWeight: 900, lineHeight: 0.94, letterSpacing: -2, color: "#fff", marginBottom: 26 },
  heroHighlight: { color: "#00e676", textShadow: "0 0 80px rgba(0,230,118,0.22)" },
  heroDescription: { fontFamily: "'DM Sans',sans-serif", fontSize: 17, lineHeight: 1.85, color: "rgba(255,255,255,0.48)", maxWidth: 560, marginBottom: 44 },
  heroButtons: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 44 },
  heroButton: { fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 1.5, background: "transparent", border: "1px solid", padding: "10px 20px", borderRadius: 100, cursor: "pointer", transition: "opacity 0.2s" },
  heroActions: { display: "flex", gap: 12, flexWrap: "wrap" },
  heroPrimary: { fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 2, background: "#00e676", color: "#000", border: "none", padding: "18px 28px", borderRadius: 8, cursor: "pointer", fontWeight: 600 },
  heroSecondary: { fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 2, background: "transparent", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.12)", padding: "18px 28px", borderRadius: 8, cursor: "pointer" },
  ticker: { borderTop: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)", overflow: "hidden", padding: "11px 0" },
  tickerContent: { display: "flex", animation: "ticker 28s linear infinite", width: "max-content" },
  tickerItems: { display: "flex", gap: 40, paddingRight: 40 },
  tickerItem: { fontFamily: "'Unbounded',sans-serif", fontSize: 11, letterSpacing: 3, color: "rgba(255,255,255,0.15)", whiteSpace: "nowrap" },
  foundation: { background: "rgba(255,255,255,0.015)", borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "72px 24px" },
  foundationGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 },
  foundationCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "22px" },
  foundationCardTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 10, letterSpacing: -0.3 },
  badge: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 2, borderRadius: 3, padding: "2px 8px", textTransform: "uppercase", display: "inline-block" },
  badgeContainer: { display: "flex", gap: 8, flexWrap: "wrap" },
  codeBlock: { background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "14px 16px", fontFamily: "'DM Mono',monospace", fontSize: 12.5, color: "#a8ff78", lineHeight: 1.8, whiteSpace: "pre-wrap", overflowX: "auto", margin: "12px 0" },
  toggle: { borderRadius: 12, overflow: "hidden", marginBottom: 12, transition: "border-color 0.25s", border: "1px solid" },
  toggleButton: { width: "100%", background: "rgba(255,255,255,0.02)", border: "none", cursor: "pointer", padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, transition: "background 0.25s" },
  toggleLabel: { fontFamily: "'Unbounded',sans-serif", fontSize: 14, fontWeight: 700, flex: 1, textAlign: "left", letterSpacing: -0.3 },
  toggleIcon: { fontSize: 20, transition: "transform 0.25s", flexShrink: 0 },
  toggleContent: { padding: "0 20px 22px", borderTop: "1px solid rgba(255,255,255,0.05)" },
  paragraph: { fontFamily: "'DM Sans',sans-serif", fontSize: 14.5, lineHeight: 1.85, margin: "0 0 14px 0" },
  listItem: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 },
  listBullet: { fontFamily: "'DM Mono',monospace", fontSize: 14, flexShrink: 0, marginTop: 1 },
  listText: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "rgba(255,255,255,0.62)", lineHeight: 1.8, margin: 0 },
  contractsGrid: { display: "flex", flexDirection: "column", gap: 12 },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 10, marginBottom: 16 },
  cardMetric: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "12px 14px" },
  cardMetricLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: 2, marginBottom: 5, textTransform: "uppercase" },
  cardMetricValue: { fontFamily: "'Unbounded',sans-serif", fontSize: 13, fontWeight: 700 },
  vLossBox: { background: "rgba(255,82,82,0.06)", border: "1px solid rgba(255,82,82,0.15)", borderRadius: 8, padding: "12px 16px", marginBottom: 10 },
  vLossLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: "#ff5252", letterSpacing: 2, marginBottom: 6, textTransform: "uppercase" },
  availableIn: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 2, marginBottom: 6, textTransform: "uppercase" },
  modeSection: { padding: "80px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  modeSectionContainer: { maxWidth: 900, margin: "0 auto", padding: "0 24px" },
  modeHeader: { display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 28, flexWrap: "wrap" },
  modeEmoji: { width: 58, height: 58, borderRadius: 14, border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 },
  modeHeaderContent: { flex: 1 },
  modeTitleRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 },
  modeTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: "clamp(22px,4vw,34px)", fontWeight: 900, letterSpacing: -0.5, margin: 0 },
  modeContracts: { display: "flex", gap: 8, flexWrap: "wrap" },
  modeSummary: { border: "1px solid", borderRadius: 14, padding: "22px 26px", marginBottom: 32 },
  vLossStatus: { display: "flex", gap: 10, alignItems: "center", marginBottom: 14 },
  triggerBadge: { marginBottom: 10 },
  martingaleStatus: { display: "flex", gap: 10, alignItems: "center", marginBottom: 14 },
  configFields: { display: "flex", flexDirection: "column", gap: 10 },
  configField: { display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 },
  configFieldName: { fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 0.5 },
  vLossIntro: { background: "rgba(255,214,0,0.07)", border: "1px solid rgba(255,214,0,0.22)", borderRadius: 14, padding: "22px 26px", marginBottom: 36 },
  vLossSubtitle: { fontFamily: "'Unbounded',sans-serif", fontSize: 16, fontWeight: 700, color: "#ffd600", marginBottom: 12 },
  vLossWarning: { background: "rgba(0,230,118,0.06)", border: "1px solid rgba(0,230,118,0.18)", borderRadius: 10, padding: "16px 18px", marginBottom: 24 },
  vLossWarningLabel: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#00e676", letterSpacing: 2, marginBottom: 8, textTransform: "uppercase" },
  vLossModes: { display: "flex", flexDirection: "column", gap: 10 },
  vLossMode: { display: "flex", flexDirection: "column", gap: 8, padding: "14px 16px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10 },
  vLossModeTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff" },
  vLossModeUses: { fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 1 },
  triggersList: { marginTop: 32 },
  triggerItem: { borderLeft: "3px solid", paddingLeft: 20, marginBottom: 36 },
  triggerName: { fontFamily: "'Unbounded',sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 10 },
  triggerDetails: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 },
  triggerDetail: { background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "12px 14px" },
  triggerDetailLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: "rgba(255,255,255,0.25)", letterSpacing: 2, marginBottom: 6, textTransform: "uppercase" },
  marketsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 },
  marketCard: { background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "16px 18px" },
  marketHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 },
  marketTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: 12, fontWeight: 700, color: "#fff" },
  uiGuideList: { display: "flex", flexDirection: "column", gap: 12 },
  uiGuideItem: { display: "flex", gap: 18, alignItems: "flex-start", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "18px 20px" },
  uiGuideIcon: { fontSize: 22, flexShrink: 0, marginTop: 2 },
  uiGuideContent: { flex: 1 },
  uiGuideHeader: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 },
  uiGuideName: { fontFamily: "'Unbounded',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff" },
  martingaleTable: { width: "100%", borderCollapse: "collapse", minWidth: 600 },
  martingaleHeadRow: { borderBottom: "1px solid rgba(255,255,255,0.1)" },
  martingaleHead: { fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 2, color: "rgba(255,255,255,0.22)", textTransform: "uppercase", padding: "12px 14px 12px 0", textAlign: "left", fontWeight: 400 },
  martingaleRow: { borderBottom: "1px solid rgba(255,255,255,0.04)" },
  martingaleCell: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, padding: "14px 14px 14px 0" },
  martingaleNote: { marginTop: 16 },
  firstSessionList: { position: "relative", paddingLeft: 0 },
  firstSessionTimeline: { position: "absolute", left: 27, top: 0, bottom: 0, width: 1, background: "rgba(0,230,118,0.12)" },
  firstSessionItem: { display: "flex", gap: 22, alignItems: "flex-start", marginBottom: 32 },
  firstSessionNumber: { width: 54, height: 54, borderRadius: "50%", background: "rgba(0,230,118,0.08)", border: "1px solid rgba(0,230,118,0.22)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Unbounded',sans-serif", fontSize: 12, fontWeight: 900, color: "#00e676", flexShrink: 0 },
  firstSessionContent: { flex: 1, paddingTop: 8 },
  firstSessionTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 8 },
  comparisonTable: { width: "100%", borderCollapse: "collapse", minWidth: 800 },
  comparisonHeadRow: { borderBottom: "1px solid rgba(255,255,255,0.1)" },
  comparisonHead: { fontFamily: "'DM Mono',monospace", fontSize: 9, letterSpacing: 2, color: "rgba(255,255,255,0.22)", textTransform: "uppercase", padding: "12px 14px 12px 0", textAlign: "left", fontWeight: 400 },
  comparisonRow: { borderBottom: "1px solid rgba(255,255,255,0.04)" },
  comparisonCell: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, padding: "16px 14px 16px 0" },
  cta: { padding: "100px 24px", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.05)", position: "relative", overflow: "hidden" },
  ctaBackground: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 400, background: "radial-gradient(ellipse, rgba(0,230,118,0.05) 0%, transparent 70%)", pointerEvents: "none" },
  ctaLabel: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: "rgba(0,230,118,0.6)", letterSpacing: 3, marginBottom: 18, textTransform: "uppercase" },
  ctaTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: "clamp(36px,7vw,72px)", fontWeight: 900, color: "#fff", letterSpacing: -1.5, marginBottom: 20, lineHeight: 1.05 },
  ctaDescription: { fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: "rgba(255,255,255,0.35)", marginBottom: 40 },
  ctaButton: { fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 2, background: "#00e676", color: "#000", border: "none", padding: "18px 28px", borderRadius: 8, cursor: "pointer", fontWeight: 600 },
  challengeContainer: { padding: "100px 24px", maxWidth: 1200, margin: "0 auto" },
  challengeTitle: { fontFamily: "'Unbounded',sans-serif", fontSize: "clamp(36px,6vw,64px)", fontWeight: 900, marginBottom: 12, color: "#fff" },
  challengeHighlight: { color: "#00e676" },
  challengeSubtitle: { fontSize: 18, color: "rgba(255,255,255,0.5)", marginBottom: 48 },
  calculatorContainer: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, padding: "clamp(20px,5vw,32px)", marginBottom: 32 },
  calculatorTitle: { fontFamily: "'Unbounded', sans-serif", fontSize: "clamp(18px,4vw,20px)", fontWeight: 700, marginBottom: 24, color: "#fff" },
  formRow: { display: "flex", gap: 20, marginBottom: 20 },
  formGroup: { flex: 1, marginBottom: 0 },
  label: { display: "block", marginBottom: 8, fontSize: 14, color: "rgba(255,255,255,0.7)", fontWeight: 500 },
  input: { width: "100%", padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 16, outline: "none", transition: "all 0.2s" },
  hint: { display: "block", marginTop: 4, fontSize: 11, color: "rgba(255,255,255,0.3)" },
  errorText: { display: "block", marginTop: 6, fontSize: 12, color: "#ff5252" },
  recommendationBox: { display: "flex", gap: 8, flexWrap: "wrap" },
  profileButton: { flex: 1, minWidth: "100px", padding: "12px", background: "rgba(255,255,255,0.03)", border: "1px solid", borderRadius: 8, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  profileRange: { fontSize: 11, color: "rgba(255,255,255,0.5)" },
  calculateButton: { width: "100%", padding: "16px", background: "#00e676", border: "none", borderRadius: 8, color: "#000", fontSize: 16, fontWeight: 700, cursor: "pointer", marginTop: 12 },
  resultsContainer: { background: "rgba(0,230,118,0.05)", border: "1px solid rgba(0,230,118,0.1)", borderRadius: 16, padding: "clamp(20px,5vw,32px)", marginBottom: 32 },
  resultsTitle: { fontFamily: "'Unbounded', sans-serif", fontSize: "clamp(18px,4vw,20px)", fontWeight: 700, marginBottom: 24, color: "#00e676" },
  metricsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16, marginBottom: 24 },
  metricCard: { padding: "clamp(12px,3vw,20px)", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, textAlign: "center" },
  metricLabel: { display: "block", fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 },
  metricValue: { display: "block", fontSize: "clamp(20px,4vw,28px)", fontWeight: 700, color: "#00e676", lineHeight: 1.2 },
  metricSub: { display: "block", fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 4 },
  progressSection: { marginTop: 20 },
  progressHeader: { display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 14, color: "rgba(255,255,255,0.6)", flexWrap: "wrap", gap: 8 },
  progressBar: { height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 4, overflow: "hidden", marginBottom: 12 },
  progressFill: { height: "100%", background: "#00e676", borderRadius: 4 },
  totalProfit: { fontSize: 14, color: "#ffd600", textAlign: "right" },
  profileInfo: { fontSize: 14, textAlign: "center", marginTop: 10, padding: 8, background: "rgba(255,255,255,0.05)", borderRadius: 8, border: "1px solid" },
  modeSelector: { marginBottom: 24, padding: "clamp(16px,4vw,24px)", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16 },
  modeSelectorHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  modeSelectorTitle: { fontSize: 18, fontWeight: 600 },
  modeGrid: { display: "grid", gap: 8 },
  modeButton: { padding: "12px 4px", background: "rgba(255,255,255,0.02)", border: "1px solid", borderRadius: 8, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, fontSize: 11 },
  modeLabel: { fontSize: 11, whiteSpace: "nowrap" },
  strategyConfig: { padding: "clamp(16px,4vw,24px)", background: "linear-gradient(145deg, rgba(0,230,118,0.05) 0%, transparent 100%)", border: "1px solid rgba(0,230,118,0.1)", borderRadius: 16, marginBottom: 24 },
  strategyTitle: { fontSize: 16, fontWeight: 600, marginBottom: 20, color: "#00e676" },
  configGrid: { display: "grid", gap: 16 },
  configItem: { display: "flex", flexDirection: "column", gap: 6 },
  configLabel: { fontSize: 11, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1 },
  configValue: { fontSize: 14, color: "#fff" },
  tableContainer: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, padding: "clamp(16px,4vw,24px)", marginBottom: 32, overflow: "hidden" },
  tableHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  tableTitle: { fontSize: "clamp(16px,4vw,18px)", fontWeight: 600, color: "#fff" },
  tableBadge: { padding: "6px 12px", background: "rgba(0,230,118,0.1)", border: "1px solid rgba(0,230,118,0.2)", borderRadius: 100, fontSize: 12, color: "#00e676" },
  tableWrapper: { overflowX: "auto", marginBottom: 20 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 1000 },
  tableHeadRow: { borderBottom: "1px solid rgba(255,255,255,0.1)" },
  tableHead: { fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", padding: "12px 8px", textAlign: "left", fontWeight: 400 },
  tableRow: { borderBottom: "1px solid rgba(255,255,255,0.03)", transition: "background 0.2s" },
  tableCell: { padding: "12px 8px", fontSize: 13 },
  dayNumber: { fontFamily: "'Unbounded', sans-serif", fontWeight: 600, color: "#fff" },
  capitalValue: { fontFamily: "'DM Mono', monospace", color: "#fff" },
  percentageValue: { fontFamily: "'DM Mono', monospace", color: "#ffd600" },
  sessionTarget: { fontFamily: "'DM Mono', monospace", color: "#00e676" },
  dailyTarget: { fontFamily: "'DM Mono', monospace", color: "#ffd600" },
  profitValue: { fontFamily: "'Unbounded', sans-serif", fontWeight: 700, color: "#00e676" },
  emptyTable: { textAlign: "center", padding: "40px", color: "rgba(255,255,255,0.3)" },
  mobileCards: { display: "flex", flexDirection: "column", gap: 12 },
  mobileCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 16 },
  mobileCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.05)" },
  mobileCardDay: { fontFamily: "'Unbounded', sans-serif", fontWeight: 600, fontSize: 14, color: "#fff" },
  mobileCardProfit: { fontFamily: "'Unbounded', sans-serif", fontWeight: 700, fontSize: 14, color: "#00e676" },
  mobileCardContent: { display: "flex", flexDirection: "column", gap: 8 },
  mobileCardRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(255,255,255,0.7)" },
  mobileCardSessions: { display: "flex", justifyContent: "space-around", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 12, color: "#00e676" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 20 },
  pageButton: { padding: "8px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 14 },
  pageInfo: { fontSize: 13, color: "rgba(255,255,255,0.5)" },
  exportSection: { display: "flex", gap: 16, marginTop: 24, justifyContent: "center", flexWrap: "wrap" },
  pdfButton: { padding: "12px 24px", background: "#00e676", border: "none", borderRadius: 8, color: "#000", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" },
  exportButton: { padding: "12px 24px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" },
  integrationContainer: { marginTop: 32 },
  footer: { borderTop: "1px solid rgba(255,255,255,0.05)", padding: "26px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  footerLogo: { fontFamily: "'Unbounded',sans-serif", fontSize: 13, fontWeight: 900, color: "#00e676" },
  footerText: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.14)", letterSpacing: 1.5, textTransform: "uppercase" },
};